import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import gsap from "gsap";
import {
  assignPerson,
  createPerson,
  updatePerson,
  fetchPerson,
  propagate,
  deleteImage,
  fetchPhoto,
  addPersonToPhoto,
  completePhoto,
  skipPhoto,
  fetchAllPhotos,
  recropPhoto,
  saveCropDraft,
  sendToEditor,
  updatePhotoAdjustments,
  addTeamMember,
  removeTeamMember,
  updatePhotoCaption,
  updatePhotoKeywords,
  applyToNonFinal,
} from "../api";
import { DevelopPanel } from "../components/AdjustmentSlider";
import IptcPanel from "../features/edit/components/IptcPanel";
import {
  DEFAULT_ADJUSTMENTS,
  isDefaultAdjustments,
  mergeWithDefaults,
} from "../utils/imageAdjustments";
import {
  initVips,
  applyVipsAdjustments,
  revokeObjectUrl,
  isVipsAvailable,
} from "../lib/vips-preview";
import {
  satiToTank,
  extractSatiNative,
} from "../lib/adjustments-map";
import { computeAdjustmentsDelta, reconstructBaseline } from "../lib/adjustments-delta";

import {
  FaceOverlay,
  FullResViewer,
  VizOverlay,
  VIZ_PRESETS,
  VIZ_PRESET_ORDER,
  getVizModeCoords,
  getEditModeCoords,
} from "../components/edit";

import {
  PhotoListSidebar,
  IncompleteModal,
  EditorModal,
  CropControls,
  CropFrame,
  FaceAssignmentBar,
  FaceThumbnailGrid,
  PersonModal,
  ImageDisplay,
} from "../features/edit/components";

// Shared photo feature components
import { PhotoToolbar } from "../features/photo";
void PhotoToolbar; // legacy import retained while the toolbar migrates per-permission to the redesign EditTopToolbar
import EditTopToolbar from "../features/edit/components/shell/EditTopToolbar";
import EditFilmstrip from "../features/edit/components/shell/EditFilmstrip";
import ModeAccordion from "../features/edit/components/shell/ModeAccordion";
import ModeAccordionItem from "../features/edit/components/shell/ModeAccordionItem";
import CaptionOrderStack from "../features/edit/components/CaptionOrderStack/CaptionOrderStack";
// FaceBar removed — Players & faces accordion now renders FaceThumbnailGrid
// directly per Variation A/B spec (no FaceAssignmentBar in the accordion).
import EditImageStage from "../features/edit/components/shell/EditImageStage";
import {
  CursorArrowRippleIcon,
  UserGroupIcon,
  TagIcon,
  AdjustmentsHorizontalIcon,
} from "@heroicons/react/24/outline";
import ConfirmDeleteImageModal from "../features/edit/components/shell/ConfirmDeleteImageModal";
import RemoveSourceModal from "../features/edit/components/shell/RemoveSourceModal";

// Extracted utilities
import { API_BASE, CENTRAL_BASE, resolveUrl, toList } from "../utils/url";
import { inferFaceRole } from "../utils/faceRole";
import {
  queuePhotoPatch,
  flushPendingForPhoto,
  subscribePhotoOutbox,
} from "../utils/photoOutbox";
import { getMatchSource } from "../api";
import { computeCropBox } from "../utils/cropCalculations";
import { extractVizMetadata } from "../utils/coordinateScaler";
import { buildPhotoListParams, filterPhotos, normalizeUrlFilters, EDITABLE_STATUSES } from "../utils/photoListUtils";
import CropMask from "../shared/components/CropMask";
import Modal from "../shared/components/ui/Modal";
import Button from "../shared/components/ui/Button";
import Spinner from "../shared/components/ui/Spinner";
import { PreviewExportNotice } from "../shared/components/feedback";
import ExportStatusPanel from "../shared/components/ExportStatusPanel";
import VoiceNotesPanel from "../shared/components/VoiceNotesPanel";
import { placeTranscript } from "../shared/lib/transcriptPlacement";
import {
  statusColors,
  getStatusLabel,
  STORAGE_KEYS,
  filterOptions,
  DEFAULT_FILTERS,
  DETECT_IMAGE_WIDTH,
  FACES_PER_PAGE,
  FACES_PER_STUB_PAGE,
} from "../utils/constants";

// Extracted hooks
import { usePersonForm, usePermissions } from "../hooks";
import { useAuth } from "../auth";
import { useLocalStorage, useLocalStorageBool } from "../hooks/useLocalStorage";
import useFaceManagement from "../hooks/useFaceManagement";
import usePhotoList from "../hooks/usePhotoList";
import useNeighborSearch from "../hooks/useNeighborSearch";
import useCropEditor from "../hooks/useCropEditor";
import { useEditKeyboardShortcuts, useEditKeyUp } from "../features/edit/hooks";
import { useUIStore } from "../stores/useUIStore";
import useUserMatches from "../hooks/useUserMatches";
import { useSetTopBar } from "../layouts/ShellContext";

const TAG_LAST_IMAGE_KEY = STORAGE_KEYS.TAG_LAST_IMAGE;

/**
 * Build minimal viz metadata when Sentinel data is missing.
 * Allows VizOverlay to render with partial data from Sati.
 */
function buildFallbackVizMetadata(metaDoc) {
  if (!metaDoc) return null;

  const sati = metaDoc?.pipeline?.sati;
  const imageDims = sati?.original_dims;
  if (!imageDims) return null;

  return {
    image_dims: { width: imageDims.width, height: imageDims.height },
    detections: [],
    crop: metaDoc?.crop || null,
    roll_info: metaDoc?.roll_info || null,
    focus_info: metaDoc?.focus_info || null,
    primary_subject: null,
  };
}

/**
 * Resolve which face the user intended to click by finding the face
 * whose center is closest to the click point, among faces whose bbox
 * contains the click. Prevents misselection when overlapping faces
 * cause the wrong DOM element to catch the click.
 */
function resolveClickedFace(e, faces, containerEl, viewMode, dims, cropBox) {
  if (!containerEl || !faces?.length) return null;

  const rect = containerEl.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return null;

  const clickXPct = ((e.clientX - rect.left) / rect.width) * 100;
  const clickYPct = ((e.clientY - rect.top) / rect.height) * 100;

  let closest = null;
  let minDist = Infinity;

  for (const face of faces) {
    if (face.ignored) continue;

    const fullFrameW = face.image_width || face.meta?.image_width;
    const fullFrameH = face.image_height || face.meta?.image_height;
    const actualCropW = face.crop_width || face.meta?.crop_width;
    const actualCropH = face.crop_height || face.meta?.crop_height;

    const { fx1, fy1, fx2, fy2, refW, refH } = viewMode === 'viz'
      ? getVizModeCoords(face, dims, fullFrameW, fullFrameH)
      : getEditModeCoords(face, dims, actualCropW, actualCropH, cropBox, fullFrameW, fullFrameH);

    if (refW <= 1 || refH <= 1) continue;
    if (fx1 === 0 && fy1 === 0 && fx2 === 0 && fy2 === 0) continue;

    const leftPct = (fx1 / refW) * 100;
    const topPct = (fy1 / refH) * 100;
    const rightPct = (fx2 / refW) * 100;
    const bottomPct = (fy2 / refH) * 100;

    // Only consider faces whose bbox contains the click
    if (clickXPct < leftPct || clickXPct > rightPct ||
        clickYPct < topPct || clickYPct > bottomPct) continue;

    const centerX = (leftPct + rightPct) / 2;
    const centerY = (topPct + bottomPct) / 2;
    const dist = Math.hypot(clickXPct - centerX, clickYPct - centerY);

    if (dist < minDist) {
      minDist = dist;
      closest = face;
    }
  }

  return closest;
}

export default function Tagging({ autoFtp = false }) {
  // Permission-based read-only mode for PHOTOGRAPHER users
  const { isPhotoReadOnly, canEditPhotos, canDeletePhotos, canApprovePhotos } = usePermissions();

  // User preferences from store
  const autoEnterCropMode = useUIStore((state) => state.autoEnterCropMode);

  // Match filtering (shared localStorage key with Overview)
  const { matches, liveMatches, isMatchLive } = useUserMatches();
  // Wire gate config rides the auth response (company.selects_require_custom_caption)
  const { user } = useAuth();
  const [matchGrouping, setMatchGrouping] = useLocalStorage(STORAGE_KEYS.MATCH_GROUPING, 'none');
  const [selectedMatchId, setSelectedMatchId] = useState(null);

  const selectedImageRef = useRef(null);
  const autoEnteredCropRef = useRef(false); // Track if we've auto-entered crop mode for current image
  // Carries the wire choice (Done + Wire) through the IncompleteModal
  // detour, so the confirm can replay the press that opened it.
  const pendingWireRef = useRef(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [metaDoc, setMetaDoc] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [hoverFace, setHoverFace] = useState(null);

  // Derive hoveredPersonId from hoverFace for bidirectional highlight
  const hoveredPersonId = hoverFace?.person || hoverFace?.person_id || null;

  // Reconcile outbox responses back into selectedPhoto. Optimistic local
  // state has already updated; this catches derived fields like
  // rendered_caption that the server recomputes on PATCH.
  useEffect(() => {
    return subscribePhotoOutbox((imageId, response) => {
      if (!response) return;
      setSelectedPhoto((prev) => {
        if (!prev || prev.image_id !== imageId) return prev;
        return { ...prev, ...response };
      });
    });
  }, []);

  // Remove bad match source modal state
  const [removeSourceTarget, setRemoveSourceTarget] = useState(null);
  const [sourceInfo, setSourceInfo] = useState(null);
  const [sourceLoading, setSourceLoading] = useState(false);

  // Ref for loadPhotos to break circular dependency with useFaceManagement
  const loadPhotosRef = useRef(null);

  // Filter/sort/search state (declared before usePhotoList which depends on them)
  const [filters, setFilters] = useLocalStorage(STORAGE_KEYS.TAG_FILTER_PREF, DEFAULT_FILTERS);
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useLocalStorage(STORAGE_KEYS.SORT_ORDER, "oldest");
  const [initialSelectedImage, setInitialSelectedImage] = useState(() => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(TAG_LAST_IMAGE_KEY) || null;
  });

  // Track recently actioned (mark-done/skip) image IDs to prevent zombie re-additions.
  // Background loadPhotos() may return the photo before the server processes the status change.
  const recentlyActionedRef = useRef(new Set());

  // Guard against double-D: track image IDs currently being completed/recropped.
  const completingRef = useRef(new Set());

  // Paginated photo list hook (replaces inline loadPhotos)
  const {
    photos,
    loading: loadingList,
    loadingMore,
    hasMore,
    error: listError,
    totalCount,
    loadMore,
    refresh: refreshPhotos,
    updatePhoto,
    removePhoto,
  } = usePhotoList({
    activeFilters: filters,
    sortOrder,
    search,
    refreshMs: 0,
    defaultStatus: EDITABLE_STATUSES,
    // Pass the live selectedImage so the backend splices it into the
    // paginated page response — otherwise a `/edit#image=<uuid>` deep
    // link to a photo on page 2+ would not be in the list and the
    // fallback effect below would snap to page-1's first photo. Cold
    // starts (no hash) still get `initialSelectedImage` from localStorage.
    includeImageId: selectedImage ?? initialSelectedImage,
    recentlyActionedRef,
    clientFilter: useCallback(
      (list) => filterPhotos(list, filters),
      [filters]
    ),
  });

  // Face management hook (extracted logic)
  const {
    faces,
    setFaces,
    selectedFace,
    setSelectedFace,
    loadingFaces,
    softDeletedPersons,
    removedFacePersons,
    loadFaces,
    handleIgnore,
    handleUnignore,
    handleUnassign,
    handleRemoveMatchSource,
    handleRemovePerson,
    handleRestorePerson,
    handleRestoreFacePerson,
    clearSoftDeletedPersons,
  } = useFaceManagement({
    selectedImage,
    loadPhotos: () => loadPhotosRef.current?.(),
    setSelectedPhoto,
    setMetaDoc,
    updatePhoto,
    selectedPhoto,
  });

  // Neighbor search hook (extracted logic)
  const {
    neighbors,
    loadingNeighbors,
    neighborsPage,
    setNeighborsPage,
    threshold,
    setThreshold,
    highConfNeighbors,
    totalNeighborPages,
    currentNeighborPage,
    pageNeighbors,
  } = useNeighborSearch({ selectedFace });

  // Image refs and bounds (needed early for useCropEditor)
  const imgRef = useRef(null);
  const imgWrapperRef = useRef(null);
  const facesContainerRef = useRef(null);
  const actualImgRef = useRef(null);
  const [imgBounds, setImgBounds] = useState(null);
  const [wrapperDims, setWrapperDims] = useState(null);
  const prevImgBoundsRef = useRef(null);
  const prevCropModeRef = useRef(false);
  const layoutOverrideRef = useRef(false);

  // Crop box calculation (needed for useCropEditor)
  const cropBox = useMemo(
    () => computeCropBox(metaDoc, selectedPhoto, selectedFace),
    [metaDoc, selectedPhoto, selectedFace]
  );

  // Build available aspect ratios from company cropping_preferences
  const availableAspects = useMemo(() => {
    const prefs = selectedPhoto?.cropping_preferences;
    if (prefs?.available_aspects?.length) return prefs.available_aspects;
    const landscape = prefs?.landscape_ratios || ['3:2'];
    const portrait = prefs?.portrait_ratios || ['2:3'];
    return [...landscape, ...portrait];
  }, [selectedPhoto?.cropping_preferences]);

  // Wire the redesign top-bar with current photo + match context.
  const setTopBar = useSetTopBar();
  useEffect(() => {
    let breadcrumb = null;
    if (selectedPhoto?.filename) {
      const matchObj = selectedPhoto.match
        ? matches?.find((m) => m.id === selectedPhoto.match)
        : null;
      const home = matchObj?.home_team_name;
      const away = matchObj?.away_team_name;
      const teams = home && away ? `${home} – ${away}` : matchObj?.name;
      breadcrumb = teams
        ? `${teams} · ${selectedPhoto.filename}`
        : selectedPhoto.filename;
    }
    setTopBar({
      breadcrumb,
      kbHints: [
        { keys: ['1', '2', '3', '4'], label: 'jump section' },
        { keys: ['⌘', '↵'], label: 'mark done' },
        { keys: ['?'], label: 'shortcuts' },
      ],
    });
    return () => setTopBar(null);
  }, [selectedPhoto?.filename, selectedPhoto?.match, matches, setTopBar]);

  // Crop editor hook (handles crop state, zoom/pan, rotation)
  const {
    cropMode,
    setCropMode,
    cropOverlay,
    cropRotation,
    setCropRotation,
    setRotationByUser,
    pendingCrop,
    setPendingCrop,
    dragState,
    exitCropMode,
    flipCropOrientation,
    cycleAspectRatio,
    setAspectByLabel,
    aspects: cropAvailableAspects,
    setFullFrameCrop,
    applyCropAlternative,
    nudgePan,
    adjustZoom,
    setZoomTo,
    handleResetCrop,
    startDrag,
    startHandleDrag,
    startRotationDrag,
    handleWheel,
    getFullFrameCrop,
    getFrameAndBase,
    getSourceDims,
    userHasModified,
    zoom,
    panX,
    panY,
    isPortrait,
    currentAspectLabel,
  } = useCropEditor({ imgBounds, metaDoc, cropBox, selectedImage, availableAspects });

  // Compute read-only mode based on permissions and photo status
  // PHOTOGRAPHER: read-only for non-done photos, can edit done photos
  const isReadOnly = useMemo(() => {
    return isPhotoReadOnly(selectedPhoto);
  }, [selectedPhoto, isPhotoReadOnly]);

  // Person form modal state (extracted hook)
  const {
    showPersonModal,
    setShowPersonModal,
    personModalPos,
    setPersonModalPos,
    phase: personModalPhase,
    query: personSearchQuery,
    setQuery: setPersonSearchQuery,
    personForm,
    setPersonForm,
    modalPersonId,
    setModalPersonId,
    personSuggestions,
    matchHubSuggestions,
    matchHubPrefill,
    // Primary team picker
    teamSearchQuery,
    setTeamSearchQuery,
    teamSuggestions,
    selectedTeam,
    selectTeam,
    clearTeam,
    // National-team toggle (create phase)
    showNationalTeamPicker,
    nationalTeamSearchQuery,
    setNationalTeamSearchQuery,
    nationalTeamSuggestions,
    selectedNationalTeam,
    toggleNationalTeamPicker,
    selectNationalTeam,
    clearNationalTeam,
    // Handlers
    openPersonModal,
    closePersonModal,
    enterCreatePhase,
    enterCreateFromMatchHub,
    enterSearchPhase,
    handleModalDragStart,
  } = usePersonForm({ matchId: selectedPhoto?.match || null });
  const [savingPerson, setSavingPerson] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null); // { text, type: 'success' | 'error' }
  const [showFullRes, setShowFullRes] = useState(false);
  const [showIncompleteModal, setShowIncompleteModal] = useState(false);
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [editorReason, setEditorReason] = useState("crop_unsatisfactory");
  const [editorReasonDetail, setEditorReasonDetail] = useState("");
  const [sendingToEditor, setSendingToEditor] = useState(false);
  const [pendingFaces, setPendingFaces] = useState([]);
  const [nextImageId, setNextImageId] = useState(null);
  // Confirmation modal state for delete action (replaces browser confirm())
  const [confirmDeleteImage, setConfirmDeleteImage] = useState(false);
  // Use localStorage hook for skip incomplete modal preference
  const [skipIncompleteModal, setSkipIncompleteModal] = useLocalStorageBool(STORAGE_KEYS.SKIP_INCOMPLETE_MODAL, false);
  const [playersOnly, setPlayersOnly] = useLocalStorageBool(STORAGE_KEYS.HIDE_SPECTATORS, true);
  const [facesPage, setFacesPage] = useState(1);
  const [showVizImage, setShowVizImage] = useState(true);
  const [showCropMask, setShowCropMask] = useState(true);
  // Viz layers preset for debug overlays (off, basic, analysis, focus)
  const [vizLayersPreset, setVizLayersPreset] = useLocalStorage(STORAGE_KEYS.VIZ_LAYERS_PRESET, 'basic');
  // Track viz image natural dimensions for accurate face overlay positioning
  const [vizNaturalDims, setVizNaturalDims] = useState(null);
  // Use localStorage hooks for UI preferences (handles persistence automatically)
  // Sidebar / faces-bar collapse state was retired with the Tab shortcut.
  // The right pane is permanently visible per the redesign spec.
  // Track new images since page load for soft refresh badge
  const initialTotalCountRef = useRef(null);
  const [newImageCount, setNewImageCount] = useState(0);

  // Develop mode state
  const [adjustments, setAdjustments] = useState({ ...DEFAULT_ADJUSTMENTS });
  // Store Sati-native values for lossless roundtrip when re-exporting
  const [satiNative, setSatiNative] = useState(null);
  // Baseline adjustments - what was loaded from DB/Sati (used to detect user changes)
  // vips preview is only triggered when adjustments differ from baseline
  const [baselineAdjustments, setBaselineAdjustments] = useState(null);
  // Use localStorage hook for adjustments clipboard (handles persistence automatically)
  const [adjustmentsClipboard, setAdjustmentsClipboard] = useLocalStorage(STORAGE_KEYS.DEVELOP_CLIPBOARD, null);
  const [savingAdjustments, setSavingAdjustments] = useState(false);

  // Sidebar tab state (Develop | IPTC)
  const [sidebarTab, setSidebarTab] = useState('iptc');
  const [savingCaption, setSavingCaption] = useState(false);

  // wasm-vips state
  const [vipsReady, setVipsReady] = useState(false);
  const [vipsProcessing, setVipsProcessing] = useState(false);
  const [vipsProcessedUrl, setVipsProcessedUrl] = useState(null);
  const vipsProcessingRef = useRef(null); // Track current processing to cancel stale requests

  // Before/After comparison state (hold backslash to show original)
  const [showOriginal, setShowOriginal] = useState(false);

  // GSAP animation refs
  const mainPanelRef = useRef(null);
  const tagPhotoRef = useRef(null);
  const tagNeighborsRef = useRef(null);
  const tagAssignRef = useRef(null);

  // URL for viz mode base image (uncropped preview - VizOverlay renders overlays client-side)
  const currentVizUrl = useMemo(() => {
    // Use uncropped preview as base - VizOverlay Canvas draws detection boxes on top
    if (selectedPhoto?.preview_url) return selectedPhoto.preview_url;
    // Fallback: construct preview URL from filename
    if (!selectedPhoto?.filename) return null;
    const stem = selectedPhoto.filename.replace(/\.[^/.]+$/, "");
    if (!stem || !selectedImage) return null;
    return `${CENTRAL_BASE.replace(/\/$/, "")}/${selectedImage}/${stem}__preview.jpg`;
  }, [selectedPhoto, selectedImage]);

  // Match-filtered photo list (mirrors Overview logic)
  const matchFilteredPhotos = useMemo(() => {
    const list = toList(photos);
    if (matchGrouping === 'my_match') {
      if (liveMatches.length === 0) return list;
      const liveIds = new Set(liveMatches.map((m) => m.id));
      return list.filter((p) => p.match && liveIds.has(p.match));
    }
    if (matchGrouping === 'by_match' && selectedMatchId !== null) {
      if (selectedMatchId === 'unassigned') return list.filter((p) => !p.match);
      return list.filter((p) => p.match === selectedMatchId);
    }
    return list;
  }, [photos, matchGrouping, liveMatches, selectedMatchId]);

  // URL for develop source image - used by wasm-vips for real-time preview
  // Prefer develop_url (2000px, unadjusted, EXIF-corrected) for fast browser editing.
  // Falls back to original_url (full-res) for photos without develop.jpg.
  const developSourceUrl = useMemo(() => {
    if (selectedPhoto?.develop_url) return resolveUrl(selectedPhoto.develop_url);
    if (selectedPhoto?.original_url) return resolveUrl(selectedPhoto.original_url);
    return null;
  }, [selectedPhoto]);

  // Viz layers computed from preset
  const vizLayers = useMemo(() => {
    return VIZ_PRESETS[vizLayersPreset] || VIZ_PRESETS.off;
  }, [vizLayersPreset]);

  // Memoize extracted viz metadata to avoid creating new object refs on every render
  const vizMetadata = useMemo(() => extractVizMetadata(metaDoc), [metaDoc]);

  // Memoized CropFrame props (replaces IIFE in JSX)
  const cropFrameProps = useMemo(() => {
    if (!cropMode) return null;
    const fb = getFrameAndBase();
    if (!fb || !currentVizUrl) return null;
    const { sourceW, sourceH } = getSourceDims();
    return { ...fb, vizUrl: currentVizUrl, sourceW, sourceH };
  }, [cropMode, getFrameAndBase, getSourceDims, currentVizUrl]);

  // Current crop dimensions in source pixels. In crop mode this tracks the
  // live frame; out of crop mode it derives from the photo's persisted
  // cropBox so the New Dimensions panel keeps showing the result after
  // confirm.
  const cropDims = useMemo(() => {
    if (cropMode) {
      const crop = getFullFrameCrop();
      if (crop && crop.length >= 4) {
        const [x1, y1, x2, y2] = crop;
        return { w: Math.round(x2 - x1), h: Math.round(y2 - y1) };
      }
    }
    if (cropBox && cropBox.x2 != null && cropBox.x1 != null) {
      return {
        w: Math.round(cropBox.x2 - cropBox.x1),
        h: Math.round(cropBox.y2 - cropBox.y1),
      };
    }
    return null;
  }, [cropMode, getFullFrameCrop, cropBox, zoom, panX, panY]);

  // Callback from ImageDisplay to update imgBounds when in local-crop mode
  const handleLayoutChange = useCallback((layout) => {
    const wasOverride = layoutOverrideRef.current;
    layoutOverrideRef.current = !!layout;
    if (layout) {
      setImgBounds(layout);
    } else if (wasOverride) {
      updateImgBounds();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // In edit view (local-crop mode), compute the crop frame position within the
  // overlay so FaceOverlay can be wrapped in a correctly sized/positioned div.
  // The overlay div uses full-image aspect ratio, but the visible crop frame
  // has the crop's aspect ratio - FaceOverlay percentages need the crop frame.
  const cropFrameBounds = useMemo(() => {
    if (showVizImage || cropMode || !cropBox || !imgBounds) return null;
    const cropW = cropBox.x2 - cropBox.x1;
    const cropH = cropBox.y2 - cropBox.y1;
    if (cropW <= 0 || cropH <= 0) return null;
    const cropAspect = cropW / cropH;
    let frameW = imgBounds.w;
    let frameH = frameW / cropAspect;
    if (frameH > imgBounds.h) {
      frameH = imgBounds.h;
      frameW = frameH * cropAspect;
    }
    return {
      x: (imgBounds.w - frameW) / 2,
      y: (imgBounds.h - frameH) / 2,
      w: frameW,
      h: frameH,
    };
  }, [showVizImage, cropMode, cropBox, imgBounds]);

  // Cycle through viz layer presets (off -> basic -> analysis -> focus -> off)
  const handleCycleVizLayers = useCallback(() => {
    const currentIndex = VIZ_PRESET_ORDER.indexOf(vizLayersPreset);
    const nextIndex = (currentIndex + 1) % VIZ_PRESET_ORDER.length;
    setVizLayersPreset(VIZ_PRESET_ORDER[nextIndex]);
  }, [vizLayersPreset, setVizLayersPreset]);

  useEffect(() => {
    selectedImageRef.current = selectedImage;
    if (typeof window !== "undefined") {
      if (selectedImage) {
        localStorage.setItem(TAG_LAST_IMAGE_KEY, selectedImage);
      } else {
        localStorage.removeItem(TAG_LAST_IMAGE_KEY);
      }
    }
  }, [selectedImage]);

  // Animate panels when photo is selected
  useEffect(() => {
    if (!selectedImage) return;
    const panels = [tagPhotoRef.current, tagNeighborsRef.current, tagAssignRef.current].filter(Boolean);
    if (panels.length) {
      gsap.fromTo(
        panels,
        { opacity: 0, x: 20 },
        { opacity: 1, x: 0, duration: 0.4, stagger: 0.08, ease: "power2.out" }
      );
    }
  }, [selectedImage]);

  // Load adjustments when selectedPhoto changes
  // Priority: 1) User adjustments from DB, 2) Sati applied settings from meta.json, 3) Defaults
  // Also set baseline so we know when user has made changes (for vips preview triggering)
  useEffect(() => {
    let loadedAdjustments;
    if (selectedPhoto?.adjustments && Object.keys(selectedPhoto.adjustments).length > 0) {
      // User has modified adjustments - use those
      loadedAdjustments = mergeWithDefaults(selectedPhoto.adjustments);
      // If we have sati_applied_settings, preserve native values for roundtrip
      if (selectedPhoto.sati_applied_settings) {
        setSatiNative(extractSatiNative(selectedPhoto.sati_applied_settings));
      } else {
        setSatiNative(null);
      }
    } else if (selectedPhoto?.sati_applied_settings) {
      // No user adjustments - load settings that Sati applied during import
      const tankSettings = satiToTank(selectedPhoto.sati_applied_settings);
      loadedAdjustments = mergeWithDefaults(tankSettings);
      setSatiNative(extractSatiNative(selectedPhoto.sati_applied_settings));
    } else {
      // No adjustments at all - use defaults
      loadedAdjustments = { ...DEFAULT_ADJUSTMENTS };
      setSatiNative(null);
    }
    setAdjustments(loadedAdjustments);
    // Set baseline - vips preview only triggers when adjustments differ from this
    // The preview.jpg already has baseline adjustments baked in by Sati.
    //
    // On an already-edited photo the loaded value is the FUSED absolute
    // (company profile + earlier edits), so taking it as the baseline would
    // make this session's delta describe only this session and quietly drop
    // the earlier movement. The stored delta lets us undo back to what the
    // editor originally started from.
    setBaselineAdjustments(
      reconstructBaseline(loadedAdjustments, selectedPhoto?.adjustments_delta),
    );
    // Clear processed URL when switching photos
    if (vipsProcessedUrl) {
      revokeObjectUrl(vipsProcessedUrl);
      setVipsProcessedUrl(null);
    }
  }, [selectedPhoto?.image_id]);

  // Initialize wasm-vips on mount (may return null if SharedArrayBuffer unavailable)
  useEffect(() => {
    let mounted = true;
    initVips()
      .then((vips) => {
        // Only set ready if vips actually initialized (not null)
        if (mounted && vips) setVipsReady(true);
      })
      .catch((err) => {
        console.error("Failed to initialize wasm-vips:", err);
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Check if user has changed adjustments from baseline (what was loaded from DB/Sati)
  // preview.jpg already has baseline adjustments baked in, so vips is only needed for changes
  const hasUserAdjustmentChanges = useMemo(() => {
    if (!baselineAdjustments || !adjustments) return false;
    // Compare serialized JSON for deep equality
    return JSON.stringify(adjustments) !== JSON.stringify(baselineAdjustments);
  }, [adjustments, baselineAdjustments]);

  // Process image with wasm-vips when user changes adjustments from baseline
  // Uses original (unadjusted) image to avoid stacking adjustments
  // Only triggers when adjustments differ from baseline - preview.jpg already has baseline baked in
  // Note: If wasm-vips is unavailable, adjustments are still saved and applied server-side by Sati
  useEffect(() => {
    // Don't process if not ready or no source
    if (!vipsReady || !developSourceUrl) {
      return;
    }

    // If adjustments match baseline (or user reset to baseline), clear vips URL
    // The preview.jpg already has baseline adjustments baked in by Sati
    if (!hasUserAdjustmentChanges) {
      if (vipsProcessedUrl) {
        revokeObjectUrl(vipsProcessedUrl);
        setVipsProcessedUrl(null);
      }
      return;
    }

    // Create a unique ID for this processing request
    const requestId = Date.now();
    vipsProcessingRef.current = requestId;

    const processImage = async () => {
      setVipsProcessing(true);
      try {
        const blob = await applyVipsAdjustments(developSourceUrl, adjustments);

        // Check if this request is still current (not superseded by a newer one)
        if (vipsProcessingRef.current !== requestId) {
          return;
        }

        // If blob is null, wasm-vips is unavailable - just clear processing state
        if (!blob) {
          setVipsProcessing(false);
          return;
        }

        // Revoke old URL before setting new one
        if (vipsProcessedUrl) {
          revokeObjectUrl(vipsProcessedUrl);
        }

        const url = URL.createObjectURL(blob);
        setVipsProcessedUrl(url);
      } catch (err) {
        console.error("wasm-vips processing failed:", err);
      } finally {
        if (vipsProcessingRef.current === requestId) {
          setVipsProcessing(false);
        }
      }
    };

    // Debounce the processing
    const timeoutId = setTimeout(processImage, 150);
    return () => clearTimeout(timeoutId);
  }, [adjustments, developSourceUrl, vipsReady]);

  // Auto-save adjustments (debounced)
  // Only saves when user has actually changed adjustments from baseline.
  // Baseline = what was loaded (Sati applied_settings or prior user adjustments).
  // This prevents Sati's applied settings from being persisted as user adjustments,
  // which would force unnecessary Sati re-export on every "Mark done".
  const saveAdjustmentsTimeoutRef = useRef(null);
  useEffect(() => {
    // Always clear pending save first (handles user reverting to baseline mid-debounce)
    if (saveAdjustmentsTimeoutRef.current) {
      clearTimeout(saveAdjustmentsTimeoutRef.current);
      saveAdjustmentsTimeoutRef.current = null;
    }

    if (!selectedImage || isDefaultAdjustments(adjustments) || !hasUserAdjustmentChanges) return;

    saveAdjustmentsTimeoutRef.current = setTimeout(async () => {
      try {
        setSavingAdjustments(true);
        await updatePhotoAdjustments(
          selectedImage,
          adjustments,
          computeAdjustmentsDelta(adjustments, baselineAdjustments),
        );
      } catch (err) {
        console.error("Failed to save adjustments:", err);
      } finally {
        setSavingAdjustments(false);
      }
    }, 1000);

    return () => {
      if (saveAdjustmentsTimeoutRef.current) {
        clearTimeout(saveAdjustmentsTimeoutRef.current);
      }
    };
  }, [adjustments, selectedImage]);

  // Develop mode handlers
  const handleCopyAdjustments = useCallback(() => {
    // useLocalStorage handles persistence automatically
    setAdjustmentsClipboard({ ...adjustments });
  }, [adjustments, setAdjustmentsClipboard]);

  const handlePasteAdjustments = useCallback(() => {
    if (adjustmentsClipboard) {
      setAdjustments(mergeWithDefaults(adjustmentsClipboard));
    }
  }, [adjustmentsClipboard]);

  const handleResetAdjustments = useCallback(async () => {
    setAdjustments({ ...DEFAULT_ADJUSTMENTS });
    // Don't update baselineAdjustments — keep original profile values.
    // zeros ≠ profile baseline → hasUserAdjustmentChanges stays true →
    // vips processes develop_url with zeros → shows raw photo without profile.
    // Explicitly save defaults to backend so stale values don't persist.
    // Delta is empty: reset means the editor withdrew every change, so the
    // movement relative to the baseline is nothing. Sending -baseline instead
    // would ask a profile-less destination to apply an inverted profile.
    if (selectedImage) {
      try {
        await updatePhotoAdjustments(selectedImage, {}, {});
      } catch (err) {
        console.error('Failed to save reset adjustments:', err);
      }
    }
  }, [selectedImage]);

  // Store refreshPhotos in ref for useFaceManagement hook (breaks circular dependency)
  loadPhotosRef.current = refreshPhotos;

  // Track initial totalCount for new image badge
  useEffect(() => {
    if (initialTotalCountRef.current === null && totalCount > 0) {
      initialTotalCountRef.current = totalCount;
    }
  }, [totalCount]);

  // Lightweight 30s count-only poll for new image badge
  useEffect(() => {
    const checkCount = async () => {
      if (initialTotalCountRef.current === null) return;
      try {
        const params = buildPhotoListParams({ search, filters });
        const data = await fetchAllPhotos(1, 0, params);
        const count = data?.count ?? 0;
        setNewImageCount(Math.max(0, count - initialTotalCountRef.current));
      } catch (_e) { /* silent */ }
    };
    const id = setInterval(checkCount, 30000);
    return () => clearInterval(id);
  }, [search, filters]);

  const clearFilters = () => {
    setFilters(["all"]);
    setSearch("");
    setMatchGrouping('none');
    setSelectedMatchId(null);
    clearUrlFilter();
  };

  // Helper to clear filter param from URL hash while preserving image param
  const clearUrlFilter = () => {
    const hash = window.location.hash || "";
    const imageMatch = hash.match(/image=([^&]+)/);
    if (imageMatch) {
      window.history.replaceState({}, "", `#image=${imageMatch[1]}`);
    } else {
      window.history.replaceState({}, "", window.location.pathname);
    }
  };

  // Parse URL hash on mount for image selection and filters
  useEffect(() => {
    const hash = window.location.hash || "";
    const match = hash.match(/image=([^&]+)/);
    if (match?.[1]) {
      setSelectedImage(decodeURIComponent(match[1]));
    } else if (initialSelectedImage) {
      setSelectedImage(initialSelectedImage);
    }
    // Check for filter in URL hash (e.g., #image=XYZ&filter=review,done)
    const filterMatch = hash.match(/filter=([^&]+)/);
    if (filterMatch?.[1]) {
      const urlFilters = normalizeUrlFilters(
        decodeURIComponent(filterMatch[1]).split(",").filter(Boolean)
      );
      if (urlFilters.length > 0) {
        setFilters(urlFilters);
      }
    }
    // Hook auto-fetches when filter/sort state changes; includeImageId ensures hash photo is in list
  }, [initialSelectedImage]);

  // Clear match selection when switching grouping modes
  useEffect(() => {
    setSelectedMatchId(null);
  }, [matchGrouping]);

  // Auto-navigate when current image is not in the match-filtered set.
  // Guard: if the list is currently loading we wait — the include-fetch
  // may still be in flight after a hash-driven selectedImage change, and
  // jumping to page-1's first photo while that resolves stomps the URL.
  useEffect(() => {
    if (loadingList) return;
    if (!matchFilteredPhotos.length || !selectedImage) return;
    const inList = matchFilteredPhotos.some((p) => p.image_id === selectedImage);
    if (!inList) {
      setSelectedImage(matchFilteredPhotos[0].image_id);
    }
  }, [matchFilteredPhotos, loadingList, selectedImage]);

  useEffect(() => {
    // loadFaces handles both null and valid imageId cases
    loadFaces(selectedImage);
    // Clear soft-deleted persons when navigating to a new image
    clearSoftDeletedPersons();
  }, [selectedImage, loadFaces, clearSoftDeletedPersons]);

  // Auto-enter crop mode when preference is enabled and image/metadata are ready
  useEffect(() => {
    // Skip if already auto-entered for this image (prevents re-entering after manual exit)
    if (autoEnteredCropRef.current) return;
    if (!autoEnterCropMode || !selectedImage || !metaDoc || !imgBounds || cropMode) return;
    // Mark as auto-entered BEFORE setting crop mode to prevent re-entry
    autoEnteredCropRef.current = true;
    // Trigger crop mode -- useCropEditor hook handles zoom/pan initialization
    setCropMode(true);
    setShowVizImage(true);
    setShowCropMask(false);
  }, [autoEnterCropMode, selectedImage, metaDoc, imgBounds, cropMode, setCropMode]);

  const toggleFilter = (key) => {
    setFilters((prev) => {
      // If clicking "all", set only "all"
      if (key === "all") {
        return ["all"];
      }
      // If currently on "all" and clicking another filter, replace with that filter
      if (prev.includes("all")) {
        return [key];
      }
      // Normal toggle behavior
      if (prev.includes(key)) {
        const next = prev.filter((k) => k !== key);
        return next.length ? next : ["all"];
      }
      return [...prev, key];
    });
    clearUrlFilter();
  };

  const currentEditedUrl = useMemo(() => {
    // Prefer cropped versions for edit mode - face overlays use crop-space coords
    // edited_preview_url = cropped preview at 1200px (fast, shows crop result)
    // edited_url = full-res cropped output (use if preview not available)
    // preview_url = original preview (UNCROPPED - avoid, will misalign face overlays!)
    if (selectedPhoto?.edited_preview_url) return selectedPhoto.edited_preview_url;
    if (selectedPhoto?.edited_url) return selectedPhoto.edited_url;
    // Last resort: uncropped preview (face overlays will be wrong)
    if (selectedPhoto?.preview_url) return selectedPhoto.preview_url;
    return null;
  }, [selectedPhoto]);

  const imgDims = useMemo(() => {
    // ==========================================================================
    // COORDINATE SYSTEM (Option 2: Full-frame coordinates)
    // ==========================================================================
    // All coordinates are in full-frame (original/edited) space.
    // Use Sati's original_dims as the authoritative source (per CLAUDE.md).
    // Both viz and edit modes use the same full-frame coordinate space.
    // ==========================================================================
    const sati = metaDoc?.pipeline?.sati;
    const exif = metaDoc?.exiftool;

    // Priority for full-frame dimensions (sati.original_dims is authoritative per CLAUDE.md):
    // 1. Sati's original_dims (V2 format)
    // 2. EXIF dimensions from metadata (for images without Sati processing)
    // 3. Face's image_width/height (set by Seraph)
    // 4. selectedPhoto dimensions (fallback)
    // NOTE: Do NOT use sentinel.image_dims - that's detect-space (2000px), not original
    const dims = {
      w: sati?.original_dims?.width || exif?.ImageWidth || selectedFace?.image_width || selectedPhoto?.image_width || selectedFace?.meta?.image_width,
      h: sati?.original_dims?.height || exif?.ImageHeight || selectedFace?.image_height || selectedPhoto?.image_height || selectedFace?.meta?.image_height,
    };
    return dims;
  }, [metaDoc, selectedFace, selectedPhoto, showVizImage]);
  // Calculate actual image bounds within the container (accounts for object-fit: contain)
  const updateImgBounds = React.useCallback(() => {
    const container = imgWrapperRef.current;
    if (!container) {
      if (!layoutOverrideRef.current) setImgBounds(null);
      return;
    }

    // Use img element for dimensions (wasm-vips outputs to img, not canvas)
    const img = actualImgRef.current;

    // Determine dimensions from the image
    let nW, nH;
    if (img?.naturalWidth && img?.naturalHeight) {
      nW = img.naturalWidth;
      nH = img.naturalHeight;
    } else {
      // Image not ready
      if (!layoutOverrideRef.current) setImgBounds(null);
      return;
    }

    // Use clientWidth/clientHeight (padding-box) not getBoundingClientRect
    // (border-box). The wrapper carries a 12 px white frame border via
    // box-sizing: border-box, so getBoundingClientRect would include the
    // border ring and offset every face/viz/crop overlay by 12 px.
    // clientWidth/clientHeight return the inner (content) area where the
    // <img> actually renders.
    const cW = container.clientWidth;
    const cH = container.clientHeight;

    // Always update wrapper dims (needed by ImageDisplay local-crop mode)
    // Bail out if values unchanged to prevent cascading re-renders
    setWrapperDims(prev =>
      prev && prev.w === cW && prev.h === cH ? prev : { w: cW, h: cH }
    );

    // Skip imgBounds when ImageDisplay manages layout (local-crop mode)
    if (layoutOverrideRef.current) return;

    // With object-fit: contain, calculate the actual rendered size and position
    // The element is scaled to fit within the container while maintaining aspect ratio
    const containerAspect = cW / cH;
    const imageAspect = nW / nH;

    let renderedW, renderedH, offsetX, offsetY;

    if (imageAspect > containerAspect) {
      // Image is wider than container - width constrained, letterboxed top/bottom
      renderedW = cW;
      renderedH = cW / imageAspect;
      offsetX = 0;
      offsetY = (cH - renderedH) / 2;
    } else {
      // Image is taller than container - height constrained, letterboxed left/right
      renderedH = cH;
      renderedW = cH * imageAspect;
      offsetX = (cW - renderedW) / 2;
      offsetY = 0;
    }

    // Include natural dimensions for face overlay calculations
    // Bail out if values unchanged to prevent cascading re-renders
    setImgBounds(prev =>
      prev && prev.x === offsetX && prev.y === offsetY &&
      prev.w === renderedW && prev.h === renderedH &&
      prev.nW === nW && prev.nH === nH
        ? prev
        : { x: offsetX, y: offsetY, w: renderedW, h: renderedH, nW, nH }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update bounds on image load and container resize
  useEffect(() => {
    const img = actualImgRef.current;
    const container = imgWrapperRef.current;

    if (img) {
      img.addEventListener("load", updateImgBounds);
      // Also update if already loaded
      if (img.complete) updateImgBounds();
    }

    // ResizeObserver for container size changes
    let resizeObserver = null;
    if (container && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateImgBounds);
      resizeObserver.observe(container);
    }

    return () => {
      if (img) img.removeEventListener("load", updateImgBounds);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [updateImgBounds, selectedPhoto, showVizImage]);

  // Recalculate image bounds when switching between viz and edit mode (or entering crop mode)
  useEffect(() => {
    prevCropModeRef.current = cropMode;
    // Small delay to ensure new image/canvas has rendered
    const timer = setTimeout(updateImgBounds, 50);
    return () => clearTimeout(timer);
  }, [showVizImage, cropMode, updateImgBounds]);

  // Track previous imgBounds for other uses
  useEffect(() => {
    prevImgBoundsRef.current = imgBounds;
  }, [imgBounds]);

  // Last caret/selection in the caption textarea: where a voice-note
  // transcript is inserted. Reset per photo (a caret from another caption
  // means nothing here).
  const captionCaretRef = useRef(null);
  useEffect(() => {
    captionCaretRef.current = null;
  }, [selectedImage]);

  const orderedNames = useMemo(() => {
    const nameMap = {};
    const displayNameMap = {};
    // The team comes from the SERVER, resolved against this match.
    //
    // This used to read `team_memberships[0]`, and that is ALPHABETICAL:
    // PersonTeamMembership orders by ['team__name', 'jersey_number']. A
    // Feyenoord player capped by Algeria therefore captioned as "of Algeria"
    // in an Eredivisie match, while PersonInImage on the same delivered file
    // said "of Feyenoord Rotterdam" -- because the exporter asked the server
    // and this did not. Stable and silent, for every player whose country
    // sorts before their club. Seen in A1C09870.jpg, 10-08-2026.
    const teamLabels = selectedPhoto?.person_team_labels || {};
    (selectedPhoto?.persons || []).forEach((p) => {
      const teamName = teamLabels[String(p.id)]?.team_name;
      nameMap[p.id] = teamName ? `${p.display_name} of ${teamName}` : p.display_name;
      displayNameMap[p.id] = p.display_name;
    });
    const usedPersonIds = new Set();

    // Build face-linked list sorted by spatial X (source-pixel space).
    // frame_bbox is immutable per matrix CLAUDE.md, so it's the stable
    // reference even if the crop changes; bbox (cropped-output space)
    // shifts when the user re-crops.
    const faceList = [];
    (toList(faces) || []).forEach((f) => {
      if (!f.person || f.ignored) return;
      const fb = Array.isArray(f.frame_bbox) ? f.frame_bbox : null;
      const bb = Array.isArray(f.bbox) ? f.bbox : null;
      if (!fb && !bb) return;
      const x = fb ? fb[0] : bb[0];
      faceList.push({
        person_id: f.person,
        label: nameMap[f.person] || f.person_name || f.person_display || f.person_id || f.id,
        displayName: displayNameMap[f.person] || f.person_name || f.person_display || '',
        face: f,
        x,
        manual: false,
        softDeleted: false,
      });
      usedPersonIds.add(f.person);
    });
    faceList.sort((a, b) => a.x - b.x);

    // Build manual_persons position map
    const manualPositions = {};
    (selectedPhoto?.manual_persons || []).forEach((m) => {
      manualPositions[String(m.person_id)] = m.position;
    });

    // Collect non-face-linked persons (manual adds)
    const manualList = [];
    (selectedPhoto?.persons || []).forEach((p) => {
      if (usedPersonIds.has(p.id)) return;
      const teamName = teamLabels[String(p.id)]?.team_name;
      manualList.push({
        person_id: p.id,
        label: teamName ? `${p.display_name} of ${teamName}` : p.display_name,
        displayName: p.display_name,
        face: null,
        position: manualPositions[String(p.id)] ?? null,
        manual: true,
        softDeleted: softDeletedPersons.has(p.id) || removedFacePersons.has(p.id),
      });
    });

    // Spatial-default merge: face-linked sorted by X, then manual persons
    // inserted at their `position`, unpositioned manual persons at the tail.
    const spatialMerged = [...faceList];
    const positioned = manualList.filter((m) => m.position !== null).sort((a, b) => a.position - b.position);
    const unpositioned = manualList.filter((m) => m.position === null);

    positioned.forEach((m, offset) => {
      const idx = Math.min(m.position + offset, spatialMerged.length);
      spatialMerged.splice(idx, 0, m);
    });
    spatialMerged.push(...unpositioned);

    // Apply manual `person_order` override: stable-sort spatialMerged so
    // entries listed in person_order appear first in that order; entries
    // not in the override fall through in their spatial position. This
    // means new tagged faces append cleanly without losing the override.
    const overrideRaw = Array.isArray(selectedPhoto?.person_order) ? selectedPhoto.person_order : [];
    const overrideRanks = new Map();
    overrideRaw.forEach((pid, i) => {
      const key = String(pid);
      if (!overrideRanks.has(key)) overrideRanks.set(key, i);
    });
    let result = spatialMerged;
    if (overrideRanks.size > 0) {
      result = [...spatialMerged]
        .map((entry, spatialIdx) => ({ entry, spatialIdx }))
        .sort((a, b) => {
          const ra = overrideRanks.has(String(a.entry.person_id))
            ? overrideRanks.get(String(a.entry.person_id))
            : Number.POSITIVE_INFINITY;
          const rb = overrideRanks.has(String(b.entry.person_id))
            ? overrideRanks.get(String(b.entry.person_id))
            : Number.POSITIVE_INFINITY;
          if (ra !== rb) return ra - rb;
          return a.spatialIdx - b.spatialIdx;
        })
        .map((x) => x.entry);
    }

    // Append removed face-linked persons not already in the list
    const resultPersonIds = new Set(result.map((r) => r.person_id));
    removedFacePersons.forEach((entry, personId) => {
      if (resultPersonIds.has(personId) || usedPersonIds.has(personId)) return;
      result.push({
        person_id: personId,
        label: entry.label,
        displayName: entry.displayName,
        face: null,
        manual: false,
        softDeleted: true,
      });
    });

    return result;
  }, [faces, selectedPhoto, softDeletedPersons, removedFacePersons]);

  const handleDeleteImage = () => {
    if (!selectedImage) return;
    setConfirmDeleteImage(true);
  };

  const confirmDeleteImageAction = async () => {
    if (!selectedImage) return;
    setConfirmDeleteImage(false);
    try {
      await deleteImage(selectedImage);
      await refreshPhotos();
      setSelectedImage(null);
      setSelectedFace(null);
      exitCropMode();
    } catch (e) {
      console.error(e);
      setSaveMessage({ text: "Failed to delete image", type: "error" });
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  // Keyboard shortcuts - extracted to reduce cognitive complexity (S3776)
  // State object for keyboard shortcuts
  const keyboardState = useMemo(() => ({
    showPersonModal,
    showIncompleteModal,
    showFullRes,
    adjustments,
    cropRotation,
    imgBounds,
    selectedImage,
    cropBox,
    metaDoc,
    selectedPhoto,
  }), [showPersonModal, showIncompleteModal, showFullRes, adjustments, cropRotation, imgBounds, selectedImage, cropBox, metaDoc, selectedPhoto]);

  // NOTE: keyboardActions moved after handleMarkDone and handleSkip definitions
  // to avoid temporal dead zone reference errors

  // Track position for inserting manual persons (does not trigger re-render)
  const pendingPersonPositionRef = useRef(null);

  // Handle remove bad match source: show confirmation modal with source info
  const handleRemoveSourceClick = async (face) => {
    setRemoveSourceTarget(face);
    setSourceInfo(null);
    setSourceLoading(true);
    try {
      const data = await getMatchSource(face.id);
      setSourceInfo(data);
    } catch (e) {
      console.error('Failed to fetch match source', e);
      setSourceInfo({ error: true });
    } finally {
      setSourceLoading(false);
    }
  };

  const confirmRemoveSource = ({ cascade = false } = {}) => {
    if (removeSourceTarget) {
      handleRemoveMatchSource(removeSourceTarget, { cascade });
    }
    setRemoveSourceTarget(null);
    setSourceInfo(null);
  };

  const cancelRemoveSource = () => {
    setRemoveSourceTarget(null);
    setSourceInfo(null);
  };

  // Wrapper for openPersonModal that also handles selectedFace state
  const handleOpenPersonModal = async ({ face = null, personId = null, position = null } = {}) => {
    // Store position for use when PersonModal completes
    pendingPersonPositionRef.current = position;
    // Allow opening modal in crop mode for face tagging
    // Clear or set selectedFace based on whether a face was passed
    // This prevents accidentally assigning to a stale selectedFace when adding "name only"
    if (face) {
      setSelectedFace(face);
    } else if (!personId) {
      // Opening modal for "add player by name only" - clear any stale selection
      setSelectedFace(null);
    }
    await openPersonModal({ face, personId });
  };

  // Handle person chip hover - find matching face and highlight it
  const handlePersonHover = useCallback((personId) => {
    if (!personId) {
      setHoverFace(null);
      return;
    }
    // Find face with this person assigned (sets hoverFace for deriving hoveredPersonId)
    const face = faces.find(f => f.person === personId || f.person_id === personId);
    setHoverFace(face || null);
  }, [faces]);

  // Memoized derived state for faces
  const currentFaces = useMemo(() => toList(faces), [faces]);

  // Per-match tagging toggle: when a match has tagging disabled (GDPR/non-public),
  // hide the Players · Faces accordion entirely. Defaults on (also while the
  // photo detail, which carries processing_plan, is still loading).
  const tagEnabled = selectedPhoto?.processing_plan?.tag !== false;

  // Sentinel detections from the per-photo metaDoc — used to infer each
  // face's role (player/referee/spectator/unknown) by containment of the
  // face frame_bbox inside a Sentinel person-body box. Sentinel runs on
  // a downscaled image (image_dims) so we scale its boxes up to source
  // pixels (original_dims) before comparing.
  const sentinelDetections = useMemo(
    () => metaDoc?.pipeline?.sentinel?.detections || [],
    [metaDoc],
  );
  const sentinelScale = useMemo(() => {
    const sentinel = metaDoc?.pipeline?.sentinel;
    const detW = sentinel?.image_dims?.width;
    const detH = sentinel?.image_dims?.height;
    const srcW = metaDoc?.original_dims?.width
      || metaDoc?.pipeline?.sati?.original_dims?.width
      || selectedPhoto?.image_width;
    const srcH = metaDoc?.original_dims?.height
      || metaDoc?.pipeline?.sati?.original_dims?.height
      || selectedPhoto?.image_height;
    if (!detW || !detH || !srcW || !srcH) return { x: 1, y: 1 };
    return { x: srcW / detW, y: srcH / detH };
  }, [metaDoc, selectedPhoto]);
  const facesWithRole = useMemo(
    () => currentFaces.map((f) => ({
      ...f,
      role: inferFaceRole(f, sentinelDetections, sentinelScale.x, sentinelScale.y),
    })),
    [currentFaces, sentinelDetections, sentinelScale],
  );
  // "Players only" — keep faces whose Sentinel-derived role is player or
  // referee; drop spectators AND unknown (no Sentinel overlap). Sentinel
  // currently emits only "player" / "referee" / "spectator" so unknown =
  // "no Sentinel detection matched" → almost always background crowd.
  const playersAccordionFaces = useMemo(
    () => (playersOnly
      ? facesWithRole.filter((f) => f.role === 'player' || f.role === 'referee')
      : facesWithRole),
    [facesWithRole, playersOnly],
  );
  const filenameDisplay = useMemo(() => {
    if (selectedPhoto?.filename) return selectedPhoto.filename;
    if (selectedImage) return selectedImage;
    return "No image selected";
  }, [selectedPhoto, selectedImage]);

  // Memoized face pagination
  const visibleFaces = useMemo(
    () => currentFaces.filter((f) => !f.ignored),
    [currentFaces]
  );
  const totalFacePages = useMemo(
    () => Math.max(1, Math.ceil((visibleFaces.length || 0) / FACES_PER_STUB_PAGE)),
    [visibleFaces.length]
  );
  const currentFacePage = useMemo(
    () => Math.min(facesPage, totalFacePages),
    [facesPage, totalFacePages]
  );
  const stubFaces = useMemo(
    () => visibleFaces.slice(
      (currentFacePage - 1) * FACES_PER_STUB_PAGE,
      currentFacePage * FACES_PER_STUB_PAGE
    ),
    [visibleFaces, currentFacePage]
  );

  // cropBox now calculated early for useCropEditor hook

  // Get crop alternatives from API (selectedPhoto.crop_info) or metadata
  const cropAlternatives = useMemo(() => {
    // Preferred: from API's crop_info.alternatives (already scaled to source-space)
    const apiAlts = selectedPhoto?.crop_info?.alternatives;
    if (Array.isArray(apiAlts) && apiAlts.length > 0) {
      return apiAlts.map((alt) => ({
        box: alt.box, // [x1, y1, x2, y2] array in source-space
        score: alt.score,
        reason: alt.reason,
      }));
    }

    // Fallback: metadata crop.alternatives (in DETECT-SPACE, needs scaling)
    const metaAlts = metaDoc?.crop?.alternatives;
    if (Array.isArray(metaAlts) && metaAlts.length > 0) {
      // Calculate scale factor from detect-space to source-space
      const detectW = metaDoc?.pipeline?.sentinel?.image_dims?.width;
      const sourceW = metaDoc?.pipeline?.sati?.original_dims?.width;
      const scale = detectW && sourceW ? sourceW / detectW : 1;

      return metaAlts.map((alt) => {
        // Normalize box format and scale to source-space
        const box = alt.box;
        if (box && typeof box === "object" && "x1" in box) {
          return {
            box: [
              Math.round(box.x1 * scale),
              Math.round(box.y1 * scale),
              Math.round(box.x2 * scale),
              Math.round(box.y2 * scale),
            ],
            score: alt.score,
            reason: alt.reason,
          };
        }
        // Array format - also needs scaling
        if (Array.isArray(box) && box.length === 4) {
          return {
            box: box.map((coord) => Math.round(coord * scale)),
            score: alt.score,
            reason: alt.reason,
          };
        }
        return { box: alt.box, score: alt.score, reason: alt.reason };
      });
    }

    return null;
  }, [selectedPhoto, metaDoc]);

  const maskCrop = useMemo(() => {
    // For pending crops, use the cropBox dimensions (already validated)
    // This ensures consistency between maskCrop and cropBox
    if (pendingCrop?.crop && cropBox) {
      // pendingCrop.crop is XYXY format: [x1, y1, x2, y2]
      const [x1 = 0, y1 = 0, x2 = 0, y2 = 0] = pendingCrop.crop;
      return {
        x1, y1, x2, y2,
        imgW: cropBox.imgW,
        imgH: cropBox.imgH,
        rotation: pendingCrop.rotation || 0,
      };
    }
    // Include stored rotation from metadata when no pending crop
    const storedRotation = metaDoc?.pipeline?.recrop?.rotation || 0;
    return cropBox ? { ...cropBox, rotation: storedRotation } : null;
  }, [pendingCrop, cropBox, metaDoc]);

  // Reset non-crop state when switching images
  // Note: Crop state (overlay, rotation, pendingCrop) is reset by useCropEditor hook
  useEffect(() => {
    // Keep old selectedPhoto and metaDoc visible under the loading spinner
    // so the user sees the previous image instead of a blank flash.
    // loadFaces will atomically swap in the new data when ready.

    // IMPORTANT: Reset imgBounds to prevent stale bounds from being used
    // This fixes the crop frame appearing in wrong position when quickly switching images
    setImgBounds(null);
    prevImgBoundsRef.current = null;
    // Reset viz dimensions to prevent stale dimensions for face overlays
    setVizNaturalDims(null);
    // Show loading indicator while new image loads
    setImageLoading(true);
    // Reset auto-entered flag so next image can auto-enter crop mode
    autoEnteredCropRef.current = false;
  }, [selectedImage]);

  const handleRecropConfirm = async () => {
    if (!selectedImage || !imgBounds) return;

    // Use shared getFullFrameCrop from useCropEditor hook
    const crop = getFullFrameCrop();
    if (!crop) return;

    setPendingCrop({ crop, rotation: cropRotation });
    exitCropMode();
    setShowVizImage(true);
    setShowCropMask(true);

    // Persist the confirmed crop as a draft in the server-side history so
    // it survives leaving the photo (fire-and-forget; Done still publishes).
    const imageId = selectedImage;
    const rotationAtConfirm = cropRotation;
    setCropHistoryIndex(null); // back to head — confirm appends a new lead
    try {
      const resp = await saveCropDraft(imageId, crop, rotationAtConfirm);
      const row = resp?.crop_version;
      if (row) {
        setSelectedPhoto((prev) =>
          prev && prev.image_id === imageId
            ? {
                ...prev,
                lead_crop: row,
                crop_history: [...(prev.crop_history || []), row].slice(-20),
              }
            : prev
        );
      }
    } catch (e) {
      console.error('crop draft save failed:', e);
      setSaveMessage({
        text: 'Could not save the crop draft — it will be lost if you leave this photo.',
        type: 'error',
      });
      setTimeout(() => setSaveMessage(null), 6000);
    }
  };

  // --- Crop history undo/redo (preview-only; Confirm appends) -------------
  // Stack floor is the sentinel auto-crop from crop_info; above it the last
  // 20 server-side history rows. Index null = at head (the lead crop).
  const cropHistoryStack = useMemo(() => {
    const floorBox = selectedPhoto?.crop_info?.box;
    const floor = Array.isArray(floorBox) && floorBox.length === 4
      ? [{ kind: 'auto', box: floorBox, rotation: 0, seq: 0 }]
      : [];
    return [...floor, ...(selectedPhoto?.crop_history || [])];
  }, [selectedPhoto?.crop_info?.box, selectedPhoto?.crop_history]);

  const [cropHistoryIndex, setCropHistoryIndex] = useState(null);
  useEffect(() => {
    setCropHistoryIndex(null);
  }, [selectedImage]);

  const applyCropHistoryEntry = (entry) => {
    if (!entry?.box) return;
    applyCropAlternative({ box: entry.box });
    setRotationByUser(entry.rotation || 0);
  };

  const handleCropHistoryUndo = () => {
    const atIdx = cropHistoryIndex ?? cropHistoryStack.length - 1;
    if (atIdx <= 0) return;
    const nextIdx = atIdx - 1;
    setCropHistoryIndex(nextIdx);
    applyCropHistoryEntry(cropHistoryStack[nextIdx]);
  };

  const handleCropHistoryRedo = () => {
    if (cropHistoryIndex == null) return;
    const nextIdx = cropHistoryIndex + 1;
    if (nextIdx > cropHistoryStack.length - 1) return;
    setCropHistoryIndex(nextIdx === cropHistoryStack.length - 1 ? null : nextIdx);
    applyCropHistoryEntry(cropHistoryStack[nextIdx]);
  };

  // Toggle crop mode with initialization logic
  const handleToggleCropMode = () => {
    const next = !cropMode;
    setCropMode(next);
    if (next) {
      setShowVizImage(true);
      setShowCropMask(false);
      // useCropEditor hook handles zoom/pan initialization from saved crop
    }
  };

  const getNextImageId = (photoList = null) => {
    const list = toList(photoList || matchFilteredPhotos);
    if (!list.length) return null;
    const idx = list.findIndex((p) => p.image_id === selectedImage);
    if (idx >= 0) {
      // Try next, then previous
      if (list[idx + 1]) return list[idx + 1].image_id;
      if (list[idx - 1]) return list[idx - 1].image_id;
    }
    // Fallback to first image in list
    return list[0]?.image_id || null;
  };

  const getPrevImageId = (photoList = null) => {
    const list = toList(photoList || matchFilteredPhotos);
    if (!list.length) return null;
    const idx = list.findIndex((p) => p.image_id === selectedImage);
    if (idx > 0) {
      return list[idx - 1].image_id;
    }
    return null; // Already at first image
  };

  // Done + Wire — the selects-only send. Visibility is a property of the
  // match's delivery plan (selects_targets non-empty), the armed/disarmed
  // state mirrors the server's caption gate for UX only — the 400 remains
  // the authority. The choice is made per completion, never sticky.
  const selectedPhotoMatch = selectedPhoto?.match
    ? matches?.find((m) => m.id === selectedPhoto.match)
    : null;
  const wireTargets = selectedPhotoMatch?.selects_targets || [];
  const wireTargetName = wireTargets.map((t) => t.name).join(', ');
  const wireCaptionOk =
    user?.company?.selects_require_custom_caption === false ||
    Boolean(selectedPhoto?.caption_override?.trim());
  const wireState = wireTargets.length === 0 ? 'hidden' : wireCaptionOk ? 'armed' : 'disarmed';

  const handleMarkDone = async () => {
    pendingWireRef.current = false;
    const pending = currentFaces.filter((f) => !f.person && !f.ignored);
    const nextId = getNextImageId();
    setNextImageId(nextId);
    if (pending.length && !skipIncompleteModal) {
      setPendingFaces(pending);
      setShowIncompleteModal(true);
      return;
    }
    await markDoneAfterIgnore(skipIncompleteModal ? pending : [], nextId);
  };

  const handleMarkDoneWire = async () => {
    if (wireState === 'hidden') return;
    if (wireState === 'disarmed') {
      // Not a dead button: the press explains itself and opens the caption
      // editor. Nothing is sent.
      setSaveMessage({
        text: `Custom caption required for ${wireTargetName || 'the wire'}. Use quick actions, the Caption Builder, or type one.`,
        type: 'error',
      });
      setTimeout(() => setSaveMessage(null), 5000);
      setSidebarTab('iptc');
      return;
    }
    pendingWireRef.current = true;
    const pending = currentFaces.filter((f) => !f.person && !f.ignored);
    const nextId = getNextImageId();
    setNextImageId(nextId);
    if (pending.length && !skipIncompleteModal) {
      setPendingFaces(pending);
      setShowIncompleteModal(true);
      return;
    }
    await markDoneAfterIgnore(skipIncompleteModal ? pending : [], nextId, { select: true });
  };

  const markDoneAfterIgnore = async (_facesToIgnore = [], nextId = null, { select = false } = {}) => {
    // Note: Backend's complete endpoint now handles ignoring unlabeled faces atomically
    // so we don't need to call _markUnlabeledIgnored separately
    if (!selectedImage) return;

    // Guard against double-D: skip if this image is already being completed
    if (completingRef.current.has(selectedImage)) return;
    completingRef.current.add(selectedImage);

    // Capture state before navigating away
    const imageToComplete = selectedImage;
    let effectivePendingCrop = pendingCrop;
    const effectiveUserModified = userHasModified;
    const effectiveCropBox = cropBox;

    // If still in crop mode with an overlay, capture it before proceeding
    if (cropMode && cropOverlay && imgBounds && !pendingCrop) {
      const crop = getFullFrameCrop();
      if (crop) {
        effectivePendingCrop = { crop, rotation: cropRotation };
      }
      exitCropMode();
    }

    // Capture pending adjustment state synchronously before navigation cancels the debounce.
    // The auto-save useEffect has a 1s debounce; optimistic navigation triggers cleanup
    // which clears the timer, so adjustments would never reach the server.
    const pendingAdjustmentSave = (
      hasUserAdjustmentChanges && !isDefaultAdjustments(adjustments)
    ) ? {
      imageId: imageToComplete,
      adjustments: { ...adjustments },
      delta: computeAdjustmentsDelta(adjustments, baselineAdjustments),
    } : null;

    // Cancel the debounce timer so the useEffect cleanup doesn't race
    if (saveAdjustmentsTimeoutRef.current) {
      clearTimeout(saveAdjustmentsTimeoutRef.current);
      saveAdjustmentsTimeoutRef.current = null;
    }

    // Register for zombie prevention before optimistic removal
    recentlyActionedRef.current.add(imageToComplete);
    setTimeout(() => recentlyActionedRef.current.delete(imageToComplete), 5000);

    // Optimistic: compute navigation target before removal, then remove
    const remaining = toList(photos).filter(p => p.image_id !== imageToComplete);
    const target = (nextId && remaining.some(p => p.image_id === nextId))
      ? nextId
      : remaining[0]?.image_id || null;
    removePhoto(imageToComplete);
    setSelectedImage(target);
    setPendingCrop(null);
    setShowIncompleteModal(false);
    setPendingFaces([]);
    setNextImageId(null);

    // Fire API calls in background (no await - UI is already on next photo)
    (async () => {
      try {
        // Flush the photo's outbox first — person_order, primary_subject,
        // and any other queued PATCHes must reach the server before we
        // mark the photo done, otherwise they get applied to a row that
        // has already transitioned out of the editable state.
        try {
          await flushPendingForPhoto(imageToComplete);
        } catch (flushErr) {
          console.error('outbox flush failed before mark-done:', flushErr);
          throw flushErr;
        }
        // Flush pending adjustments to server before complete/recrop.
        // _handle_done_transition reads photo.adjustments from DB, so they must be saved first.
        if (pendingAdjustmentSave) {
          await updatePhotoAdjustments(
            pendingAdjustmentSave.imageId,
            pendingAdjustmentSave.adjustments,
            pendingAdjustmentSave.delta
          );
        }
        // Decision 27-08: Done publishes the LEAD draft even when this
        // session didn't touch the crop — a draft confirmed in the app (or
        // another Tank session) ships on this Done. Only consult the server
        // when there is no local pending crop; the refreshed lead wins iff
        // it is an unpublished draft.
        if (!effectivePendingCrop) {
          try {
            const fresh = await fetchPhoto(imageToComplete);
            const lead = fresh?.lead_crop;
            const publishedSeq = fresh?.last_published_seq;
            if (
              lead?.kind === 'draft' &&
              Array.isArray(lead.box) &&
              (publishedSeq == null || lead.seq > publishedSeq)
            ) {
              effectivePendingCrop = { crop: lead.box, rotation: lead.rotation || 0 };
              setSaveMessage({
                text: `Shipping crop v${lead.seq}${lead.client && lead.client !== 'tank' ? ` (draft from ${lead.client})` : ''}.`,
                type: 'success',
              });
              setTimeout(() => setSaveMessage(null), 4000);
            }
          } catch (e) {
            // Server unreachable = fall back to today's behaviour (complete
            // with whatever is local); never block a Done on the refresh.
            console.error('lead-crop refresh before done failed:', e);
          }
        }

        let didRecrop = false;
        if (effectivePendingCrop) {
          // Server short-circuits to {noop: true} when the incoming crop +
          // rotation match what's already on disk (user dragged the crop
          // and put it back, or clicked Save without touching it). On
          // noop, fall through to completePhoto so the photo still
          // advances to done — the no-op response means "nothing to
          // recrop; you probably want complete instead".
          const recropResp = await recropPhoto(
            imageToComplete,
            effectivePendingCrop.crop,
            effectivePendingCrop.rotation,
            autoFtp,
            select,
          );
          didRecrop = !recropResp?.noop;
        }
        if (!didRecrop) {
          const approvedCrop = effectiveCropBox != null;
          await completePhoto(imageToComplete, autoFtp, approvedCrop, select);
        }
        // Silently refresh photo list in background
        refreshPhotos();
      } catch (e) {
        console.error('Failed to complete photo:', e);
        const shortId = imageToComplete.slice(0, 8);
        // A wire-gate 400 carries its own explanation (caption cleared in a
        // race, wire target removed mid-match) — show it over the generic
        // message so the editor knows what to fix, not just that it failed.
        const gateError = e?.response?.data?.code ? e.response.data.error : null;
        setSaveMessage({
          text: gateError
            ? `${gateError} (${shortId} is back in the list.)`
            : `Failed to save ${shortId}. It will reappear in the list.`,
          type: 'error',
        });
        // Re-add photo by refreshing from server
        refreshPhotos();
        setTimeout(() => setSaveMessage(null), 6000);
      } finally {
        completingRef.current.delete(imageToComplete);
      }
    })();
  };

  const handleSkip = async () => {
    if (!selectedImage) return;
    const imageToSkip = selectedImage;

    // Get next neighbor before filtering (same pattern as markDoneAfterIgnore)
    const preferredNext = getNextImageId();

    // Register for zombie prevention before optimistic removal
    recentlyActionedRef.current.add(imageToSkip);
    setTimeout(() => recentlyActionedRef.current.delete(imageToSkip), 5000);

    // Optimistic: compute navigation target before removal, then remove
    const remaining = toList(photos).filter(p => p.image_id !== imageToSkip);
    const target = (preferredNext && remaining.some(p => p.image_id === preferredNext))
      ? preferredNext
      : remaining[0]?.image_id || null;
    removePhoto(imageToSkip);
    setSelectedImage(target);

    // Fire API in background
    (async () => {
      try {
        await skipPhoto(imageToSkip);
        refreshPhotos();
      } catch (e) {
        console.error('Failed to skip photo:', e);
        const shortId = imageToSkip.slice(0, 8);
        setSaveMessage({ text: `Failed to skip ${shortId}. It will reappear in the list.`, type: 'error' });
        refreshPhotos();
        setTimeout(() => setSaveMessage(null), 5000);
      }
    })();
  };

  // Actions object for keyboard shortcuts
  // NOTE: Must be defined after handleMarkDone and handleSkip to avoid temporal dead zone
  const keyboardActions = useMemo(() => ({
    setShowFullRes,
    handleCopyAdjustments,
    handlePasteAdjustments,
    setShowOriginal,
    handleCycleVizLayers,
    handleMarkDone,
    handleMarkDoneWire,
    handleSkip,
    isDefaultAdjustments,
    handleRecropConfirm,
    handleResetCrop,
    setFullFrameCrop,
    nudgePan,
    flipCropOrientation,
    setRotationByUser,
    enterCropMode: () => {
      setCropMode(true);
      setShowVizImage(true);
      // useCropEditor hook handles zoom/pan initialization
    },
    getPrevImageId,
    getNextImageId,
    setSelectedImage,
    openAccordionSection: (itemKey) => setSidebarTab(itemKey),
  }), [handleCopyAdjustments, handlePasteAdjustments, handleCycleVizLayers, handleMarkDone, handleMarkDoneWire, handleSkip, handleRecropConfirm, handleResetCrop, setFullFrameCrop, nudgePan, flipCropOrientation, setRotationByUser, getPrevImageId, getNextImageId, setCropMode, setSidebarTab]);

  // Use extracted keyboard shortcuts hook
  useEditKeyboardShortcuts({
    enabled: true,
    cropMode,
    state: keyboardState,
    actions: keyboardActions,
  });

  // Keyup handler for before/after comparison (release backslash to return to adjusted)
  useEditKeyUp(useCallback(() => setShowOriginal(false), []));

  const handleSendToEditor = async () => {
    if (!selectedImage) return;
    setSendingToEditor(true);
    try {
      // Get current crop if in crop mode (XYXY format)
      const cropBoxArray = cropBox ? [cropBox.x1, cropBox.y1, cropBox.x2, cropBox.y2] : null;
      const currentCrop = pendingCrop?.crop || cropBoxArray;
      // Get tagged person IDs
      const personIds = faces
        ?.filter(f => f.person && !f.ignored)
        ?.map(f => f.person.id || f.person_id)
        ?.filter(Boolean) || [];

      await sendToEditor(selectedImage, editorReason, editorReasonDetail, currentCrop, personIds);

      // Pre-compute next image before refresh (photo just sent to editor will leave current filter)
      const nextId = getNextImageId();
      await refreshPhotos();
      // If staying on same image, fetch fresh data
      if (nextId === selectedImage) {
        const updatedPhoto = await fetchPhoto(selectedImage);
        if (updatedPhoto) {
          setSelectedPhoto(updatedPhoto);
        }
      }
      setSelectedImage(nextId);

      // Reset modal state
      setShowEditorModal(false);
      setEditorReason("crop_unsatisfactory");
      setEditorReasonDetail("");
    } catch (e) {
      console.error(e);
      const errorMsg = "Failed to send to editor: " + (e.response?.data?.error || e.message);
      setSaveMessage({ text: errorMsg, type: "error" });
      setTimeout(() => setSaveMessage(null), 5000);
    } finally {
      setSendingToEditor(false);
    }
  };

  // Show centered spinner while initially loading the photo list
  if (loadingList && toList(photos).length === 0) {
    return (
      <div className="page page-tagging" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spinner size="lg" />
      </div>
    );
  }

  return (
      <div className="page page-tagging">
      {/* Toast notification for save messages */}
      {saveMessage && (
        <div
          className={`toast-message toast-${saveMessage.type}`}
          style={{
            position: "fixed",
            top: 80,
            left: "50%",
            transform: "translateX(-50%)",
            padding: "12px 24px",
            borderRadius: 8,
            background: saveMessage.type === "success" ? "#1a472a" : "#4a1a1a",
            border: `1px solid ${saveMessage.type === "success" ? "#66ffb7" : "#ff6b6b"}`,
            color: saveMessage.type === "success" ? "#66ffb7" : "#ff6b6b",
            fontSize: 14,
            fontWeight: 500,
            zIndex: 9999,
            boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
            animation: "fadeIn 0.2s ease-out",
          }}
        >
          {saveMessage.text}
        </div>
      )}
      <IncompleteModal
        isOpen={showIncompleteModal}
        pendingFacesCount={pendingFaces.length}
        onCancel={() => {
          setShowIncompleteModal(false);
          setPendingFaces([]);
          setNextImageId(null);
          pendingWireRef.current = false;
        }}
        onConfirm={async () => {
          await markDoneAfterIgnore(pendingFaces, nextImageId, {
            select: pendingWireRef.current,
          });
        }}
      />

      <EditorModal
        isOpen={showEditorModal}
        reason={editorReason}
        reasonDetail={editorReasonDetail}
        sending={sendingToEditor}
        onReasonChange={setEditorReason}
        onReasonDetailChange={setEditorReasonDetail}
        onSend={handleSendToEditor}
        onClose={() => setShowEditorModal(false)}
      />

      <ConfirmDeleteImageModal
        isOpen={confirmDeleteImage}
        imageId={selectedImage}
        onCancel={() => setConfirmDeleteImage(false)}
        onConfirm={confirmDeleteImageAction}
      />

      <RemoveSourceModal
        isOpen={!!removeSourceTarget}
        loading={sourceLoading}
        sourceInfo={sourceInfo}
        onCancel={cancelRemoveSource}
        onConfirm={confirmRemoveSource}
      />

      {/* Tank Redesign Editor variant A — single column with bottom filmstrip.
          PhotoListSidebar (left column in the previous shell) is replaced by
          EditFilmstrip below the canvas; same selection model + photo list. */}
      <div className="edit-frame">
        <div className="content">
          {!loadingList && toList(photos).length === 0 ? (
            <div className="empty-state" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", padding: 40 }}>
              <div className="empty-message">No results found</div>
              <div className="empty-sub" style={{ marginBottom: 20 }}>Try adjusting your filters or clearing them to see all photos.</div>
              <div className="row gap">
                <button className="ghost" onClick={clearFilters}>Clear filters</button>
                <button className="ghost" onClick={() => { window.location.href = "/overview"; }}>Back to Overview</button>
              </div>
            </div>
          ) : (
          <div className="tagging-main">
            <div className="card tag-photo" ref={tagPhotoRef}>
              <EditTopToolbar
                filename={filenameDisplay}
                isReadOnly={isReadOnly}
                saving={false}
                canApprovePhotos={canApprovePhotos}
                onMarkDone={handleMarkDone}
                onMarkDoneWire={handleMarkDoneWire}
                wireState={wireState}
                wireTargetName={wireTargetName}
                onSkip={handleSkip}
                onResetCrop={cropMode ? handleResetCrop : null}
                kbHint={{ label: 'arrows', keys: ['←', '→'] }}
              />

              <ExportStatusPanel
                imageId={selectedImage}
                photoStatus={selectedPhoto?.status}
              />

              <VoiceNotesPanel
                matchId={selectedPhoto?.match || null}
                imageId={selectedImage}
                onInsert={(text) => {
                  // The transcript lands where the editor put the caret in
                  // the caption, else in the persons slot (where the Caption
                  // Builder writes), else at the end. Saved through the same
                  // optimistic path the Caption Builder uses.
                  const base = selectedPhoto?.caption_override || selectedPhoto?.rendered_caption || '';
                  const personsPhrase = (orderedNames || [])
                    .filter((n) => !n.softDeleted)
                    .map((n) => n.label)
                    .join(', ');
                  const { caption: next } = placeTranscript(base, text, {
                    selection: captionCaretRef.current,
                    personsPhrase,
                  });
                  if (next === base) return;
                  setSelectedPhoto((prev) => (prev ? { ...prev, caption_override: next } : prev));
                  queuePhotoPatch(selectedImage, { caption_override: next }, { endpoint: 'caption' });
                }}
              />

              {loadingFaces && <div className="loading-line">Loading faces...</div>}

              <EditImageStage
                imgRef={imgRef}
                imgWrapperRef={imgWrapperRef}
                actualImgRef={actualImgRef}
                facesContainerRef={facesContainerRef}
                vipsProcessedUrl={vipsProcessedUrl}
                currentVizUrl={currentVizUrl}
                currentEditedUrl={currentEditedUrl}
                hasAdjustments={!isDefaultAdjustments(adjustments)}
                vipsProcessing={vipsProcessing}
                vipsReady={vipsReady}
                showVizImage={showVizImage}
                showOriginal={showOriginal}
                developSourceUrl={developSourceUrl}
                pendingCrop={pendingCrop}
                cropBox={cropBox}
                metaDoc={metaDoc}
                wrapperDims={wrapperDims}
                onLayoutChange={handleLayoutChange}
                onImageLoad={(e) => {
                  updateImgBounds();
                  setImageLoading(false);
                  // Capture natural dimensions for accurate face overlay positioning in viz mode.
                  // Only update when showing viz image to avoid using edited (cropped) dimensions.
                  if (e?.target && showVizImage) {
                    setVizNaturalDims({ w: e.target.naturalWidth, h: e.target.naturalHeight });
                  }
                }}
                onImageError={() => setImageLoading(false)}
                imageLoading={imageLoading}
                imgBounds={imgBounds}
                cropFrameBounds={cropFrameBounds}
                cropMode={cropMode}
                cropFrameProps={cropFrameProps}
                zoom={zoom}
                panX={panX}
                panY={panY}
                cropRotation={cropRotation}
                onStartDrag={startDrag}
                onWheel={handleWheel}
                onZoomIn={() => adjustZoom(1.15)}
                onZoomOut={() => adjustZoom(1 / 1.15)}
                onHandleDrag={startHandleDrag}
                onRotationDrag={startRotationDrag}
                showCropMask={showCropMask}
                maskCrop={maskCrop}
                vizLayersPreset={vizLayersPreset}
                vizMetadata={vizMetadata}
                vizLayers={vizLayers}
                currentFaces={playersAccordionFaces}
                selectedFace={selectedFace}
                hoverFace={hoverFace}
                hoveredPersonId={hoveredPersonId}
                showPersonModal={showPersonModal}
                imgDims={imgDims}
                vizNaturalDims={vizNaturalDims}
                resolveClickedFace={resolveClickedFace}
                onSelectFace={setSelectedFace}
                onUnassignFace={handleUnassign}
                onIgnoreFace={handleIgnore}
                onUnignoreFace={handleUnignore}
                onRemoveSource={handleRemoveSourceClick}
                onHoverFace={setHoverFace}
                onOpenPersonModal={handleOpenPersonModal}
                selectedPhoto={selectedPhoto}
              />
            </div>

            {/* Tank Redesign Editor variant A — stacked accordion (4 sections).
                Source: design_handoff_tank/screens-editor.jsx § Screen04EditorA.
                Crop + Players sections currently summarise the surfaces that
                still live below the canvas (CropControls in EditImageStage,
                FaceBar at the bottom). Future PRs will move those into the
                accordion sections proper. */}
            <div
              ref={tagNeighborsRef}
              className="card tag-neighbors"
            >
              <ModeAccordion
                active={sidebarTab}
                onActiveChange={(key) => key && setSidebarTab(key)}
              >
                <ModeAccordionItem
                  itemKey="crop"
                  n="1"
                  title="Crop"
                  icon={<CursorArrowRippleIcon style={{ width: 14, height: 14 }} />}
                  summary={
                    cropDims
                      ? `${cropDims.w} × ${cropDims.h} · ${currentAspectLabel || ""}`.trim().replace(/·\s*$/, "")
                      : cropMode ? "Crop mode" : "View"
                  }
                >
                  <CropControls
                    cropRotation={cropRotation}
                    aspectLabel={currentAspectLabel}
                    availableAspects={cropAvailableAspects}
                    onSetAspect={setAspectByLabel}
                    onCycleAspect={cycleAspectRatio}
                    cropDims={cropDims}
                    cropBox={cropBox}
                    outputConfig={user?.company || null}
                    onResetCrop={handleResetCrop}
                    cropAlternatives={cropAlternatives}
                    rollInfo={metaDoc?.roll_info}
                    zoom={zoom}
                    onZoomChange={setZoomTo}
                    onRotationChange={(v) => {
                      setRotationByUser(v);
                      localStorage.setItem(STORAGE_KEYS.CROP_STEP, String(v));
                    }}
                    onApplyAlternative={applyCropAlternative}
                    cropHistory={cropHistoryStack}
                    cropHistoryIndex={cropHistoryIndex}
                    onCropHistoryUndo={handleCropHistoryUndo}
                    onCropHistoryRedo={handleCropHistoryRedo}
                    lastPublishedSeq={selectedPhoto?.last_published_seq ?? null}
                    disabled={!cropMode}
                  />
                </ModeAccordionItem>

                {tagEnabled && (
                <ModeAccordionItem
                  itemKey="players"
                  n="2"
                  title="Players · Faces"
                  icon={<UserGroupIcon style={{ width: 14, height: 14 }} />}
                  summary={
                    currentFaces?.length
                      ? `${(orderedNames || []).filter((n) => !n.softDeleted).length} of ${currentFaces.length} linked`
                      : "No faces"
                  }
                >
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 6,
                      padding: '0 0 8px',
                      fontFamily: 'var(--rd-font-mono)',
                      fontSize: 'var(--rd-type-eyebrow)',
                      letterSpacing: 'var(--rd-track-eyebrow)',
                      textTransform: 'uppercase',
                      color: 'var(--rd-color-text-subtle)',
                      userSelect: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={!!playersOnly}
                      onChange={(e) => setPlayersOnly(e.target.checked)}
                      style={{
                        width: 12,
                        height: 12,
                        margin: 0,
                        accentColor: 'var(--rd-color-accent)',
                        cursor: 'pointer',
                      }}
                    />
                    <span>Players only</span>
                  </label>
                  <CaptionOrderStack
                    entries={orderedNames}
                    selectedFaceId={selectedFace?.id}
                    hoveredPersonId={hoveredPersonId}
                    hasManualOverride={Array.isArray(selectedPhoto?.person_order) && selectedPhoto.person_order.length > 0}
                    resolveUrl={resolveUrl}
                    onReorder={(nextOrder) => {
                      setSelectedPhoto((prev) => (prev ? { ...prev, person_order: nextOrder } : prev));
                      queuePhotoPatch(selectedImage, { person_order: nextOrder });
                    }}
                    onResetOrder={() => {
                      setSelectedPhoto((prev) => (prev ? { ...prev, person_order: [] } : prev));
                      queuePhotoPatch(selectedImage, { person_order: [] });
                    }}
                    primarySubjectPersonId={selectedPhoto?.primary_subject_person_id || null}
                    onSetPrimary={(personId) => {
                      setSelectedPhoto((prev) => (prev ? { ...prev, primary_subject_person_id: personId } : prev));
                      queuePhotoPatch(selectedImage, { primary_subject_person_id: personId || null });
                    }}
                    onSelectEntry={(entry) => {
                      if (entry.face) handleOpenPersonModal({ face: entry.face });
                      else handleOpenPersonModal({ face: null, personId: entry.person_id });
                    }}
                    onSkipEntry={(entry) => {
                      // Face entry → soft-delete the face (ignored = true).
                      // Manual / soft-deleted-face entry → fully remove the
                      // person from this photo (also strips from
                      // manual_persons server-side).
                      if (entry.face) {
                        handleIgnore(entry.face);
                      } else if (entry.person_id) {
                        handleRemovePerson(entry.person_id);
                      }
                    }}
                    onHoverPerson={(personId) => {
                      if (!personId) {
                        setHoverFace(null);
                        return;
                      }
                      const f = (toList(faces) || []).find((face) => face.person === personId);
                      if (f) setHoverFace(f);
                    }}
                    onAddFace={() => handleOpenPersonModal({ face: null, personId: null })}
                    onAutoRelink={null}
                    autoRelinkEnabled={false}
                  />
                </ModeAccordionItem>
                )}

                <ModeAccordionItem
                  itemKey="iptc"
                  n="3"
                  title="IPTC · Caption builder"
                  icon={<TagIcon style={{ width: 14, height: 14 }} />}
                  summary={
                    savingCaption
                      ? "Saving…"
                      : selectedPhoto?.caption_override
                        ? "Custom caption"
                        : "Auto"
                  }
                >
                  <IptcPanel
                    imageId={selectedImage}
                    matchId={selectedPhoto?.match || null}
                    cropDims={cropDims}
                    outputConfig={user?.company || null}
                    persons={selectedPhoto?.persons || []}
                    captionOverride={selectedPhoto?.caption_override || ''}
                    captionChains={selectedPhoto?.caption_chains || null}
                    renderedCaption={selectedPhoto?.rendered_caption || ''}
                    orderedNames={orderedNames}
                    onCaptionSelection={(sel) => { captionCaretRef.current = sel; }}
                    primarySubjectPersonId={selectedPhoto?.primary_subject_person_id || null}
                    capturedAt={selectedPhoto?.capture_date || null}
                    matchPhase={selectedPhoto?.match_phase || null}
                    nearbyEvent={selectedPhoto?.nearby_event || null}
                    scoreAtCapture={selectedPhoto?.score_at_capture || null}
                    photographer={selectedPhoto?.photographer || null}
                    keywords={selectedPhoto?.keywords_override ?? selectedPhoto?.rendered_keywords ?? []}
                    onCaptionChange={(newCaption, newChains) => {
                      // Optimistic: update local state immediately so the
                      // caption preview reflects the click without waiting
                      // for the server. PATCH is queued via the outbox.
                      setSelectedPhoto((prev) => (prev ? {
                        ...prev,
                        caption_override: newCaption || null,
                        caption_chains: newChains !== undefined ? newChains : prev.caption_chains,
                      } : prev));
                      const fields = { caption_override: newCaption || null };
                      if (newChains !== undefined) fields.caption_chains = newChains;
                      queuePhotoPatch(selectedImage, fields, { endpoint: 'caption' });
                    }}
                    onTogglePrimary={(personId) => {
                      // Mirror the CaptionOrderStack star — same field, same
                      // outbox, so both surfaces stay in sync without extra
                      // glue.
                      setSelectedPhoto((prev) => (prev ? {
                        ...prev,
                        primary_subject_person_id: personId,
                      } : prev));
                      queuePhotoPatch(selectedImage, {
                        primary_subject_person_id: personId,
                      });
                    }}
                    onCaptionReset={() => {
                      setSelectedPhoto((prev) => (prev ? {
                        ...prev,
                        caption_override: null,
                        caption_chains: null,
                      } : prev));
                      queuePhotoPatch(
                        selectedImage,
                        { caption_override: null, caption_chains: null },
                        { endpoint: 'caption' },
                      );
                    }}
                    onReorderPersons={(nextOrder) => {
                      setSelectedPhoto((prev) => (prev ? { ...prev, person_order: nextOrder } : prev));
                      queuePhotoPatch(selectedImage, { person_order: nextOrder });
                    }}
                    onKeywordsChange={(newKeywords) => {
                      const next = newKeywords.length > 0 ? newKeywords : null;
                      setSelectedPhoto((prev) => (prev ? {
                        ...prev,
                        keywords_override: next,
                      } : prev));
                      queuePhotoPatch(
                        selectedImage,
                        { keywords_override: next },
                        { endpoint: 'keywords' },
                      );
                    }}
                    disabled={!selectedImage || isReadOnly}
                    saving={savingCaption}
                  />
                </ModeAccordionItem>

                <ModeAccordionItem
                  itemKey="develop"
                  n="4"
                  title="Develop"
                  icon={<AdjustmentsHorizontalIcon style={{ width: 14, height: 14 }} />}
                  summary={
                    savingAdjustments
                      ? "Saving…"
                      : isDefaultAdjustments(adjustments)
                        ? "Default"
                        : "Custom"
                  }
                >
                  <DevelopPanel
                    adjustments={adjustments}
                    baselineAdjustments={baselineAdjustments}
                    onChange={setAdjustments}
                    onReset={handleResetAdjustments}
                    onCopy={handleCopyAdjustments}
                    onPaste={handlePasteAdjustments}
                    hasClipboard={!!adjustmentsClipboard}
                    disabled={!selectedImage || isReadOnly}
                    appliedProfileId={selectedPhoto?.applied_profile_id}
                    onApplyProfile={(profile) => {
                      if (profile.adjustments) {
                        setAdjustments({ ...DEFAULT_ADJUSTMENTS, ...profile.adjustments });
                      }
                    }}
                    onShowOriginal={setShowOriginal}
                  />
                  {selectedPhoto?.match && !isDefaultAdjustments(adjustments) && (
                    <div style={{
                      marginTop: 12,
                      paddingTop: 12,
                      borderTop: "1px solid var(--rd-color-line)",
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                    }}>
                      <button
                        type="button"
                        className="ghost"
                        onClick={async () => {
                          try {
                            const result = await applyToNonFinal({
                              matchId: selectedPhoto.match,
                              settings: { adjustments },
                            });
                            setSaveMessage({
                              text: `Applied to ${result.updated_count} non-final photo(s).`,
                              type: "success",
                            });
                            setTimeout(() => setSaveMessage(null), 4000);
                          } catch (e) {
                            console.error("apply_to_non_final failed", e);
                            setSaveMessage({
                              text: "Apply failed. See console.",
                              type: "error",
                            });
                            setTimeout(() => setSaveMessage(null), 4000);
                          }
                        }}
                        disabled={isReadOnly}
                        title="Push these develop settings to every Pending / Attention photo in this match. Done / Exported / Skipped photos are never overwritten."
                      >
                        Apply to non-final
                      </button>
                      <span style={{
                        fontFamily: "var(--rd-font-sans)",
                        fontSize: "var(--rd-type-body-micro)",
                        color: "var(--rd-color-text-faint)",
                        lineHeight: 1.5,
                      }}>
                        Pushes these settings to Pending and Attention photos
                        in the current match. Done, Exported, and Skipped
                        photos are never overwritten.
                      </span>
                    </div>
                  )}
                </ModeAccordionItem>
              </ModeAccordion>
            </div>

          </div>
          )}
        </div>

        <EditFilmstrip
          photos={matchFilteredPhotos}
          selectedImageId={selectedImage}
          onSelectPhoto={setSelectedImage}
          resolveUrl={resolveUrl}
          onLoadMore={loadMore}
          hasMore={hasMore}
          loadingMore={loadingMore}
        />
      </div>

      <PersonModal
        isOpen={showPersonModal}
        position={personModalPos}
        phase={personModalPhase}
        query={personSearchQuery}
        onQueryChange={setPersonSearchQuery}
        personForm={personForm}
        personSuggestions={personSuggestions}
        matchHubSuggestions={matchHubSuggestions}
        onSelectMatchHubSuggestion={enterCreateFromMatchHub}
        highConfNeighbors={highConfNeighbors}
        selectedFace={selectedFace}
        savingPerson={savingPerson}
        // Primary team picker
        teamSearchQuery={teamSearchQuery}
        teamSuggestions={teamSuggestions}
        selectedTeam={selectedTeam}
        onTeamSearchChange={setTeamSearchQuery}
        onSelectTeam={selectTeam}
        onClearTeam={clearTeam}
        // National-team toggle
        showNationalTeamPicker={showNationalTeamPicker}
        onToggleNationalTeamPicker={toggleNationalTeamPicker}
        nationalTeamSearchQuery={nationalTeamSearchQuery}
        nationalTeamSuggestions={nationalTeamSuggestions}
        selectedNationalTeam={selectedNationalTeam}
        onNationalTeamSearchChange={setNationalTeamSearchQuery}
        onSelectNationalTeam={selectNationalTeam}
        onClearNationalTeam={clearNationalTeam}
        // Phase transitions
        onEnterCreatePhase={enterCreatePhase}
        onEnterSearchPhase={enterSearchPhase}
        // Form handlers
        onFormChange={setPersonForm}
        onSelectSuggestion={async (p) => {
          // P5: search-phase click = direct assign + close. Editor doesn't
          // need to also Save — picking the person IS the action.
          if (!p?.id) return;
          setSavingPerson(true);
          setSaveMessage(null);
          try {
            const totalFaces = faces.filter((f) => !f.ignored).length;
            const labeledBefore = faces.filter((f) => !f.ignored && f.person).length;
            // Only propagate on first-time tagging — re-assigning an already
            // tagged face shouldn't re-fire propagation (could amplify a
            // mis-tag against similar faces).
            const willPropagate = !!selectedFace?.id && !selectedFace.person;

            if (selectedFace?.id) {
              await assignPerson(selectedFace.id, p.id, null);
            } else if (selectedImage) {
              await addPersonToPhoto(selectedImage, p.id, pendingPersonPositionRef.current);
            }
            pendingPersonPositionRef.current = null;
            loadFaces(selectedImage).catch((e) => console.error('Face refresh failed', e));
            refreshPhotos();
            setShowPersonModal(false);
            setModalPersonId(null);

            const labeledAfter = Math.min(labeledBefore + 1, totalFaces);
            const personName = p.display_name || p.first_name || 'person';
            const initialMsg = willPropagate
              ? `Saved! ${labeledAfter}/${totalFaces} faces labeled. Propagating...`
              : `Saved! Assigned to ${personName}.`;
            setSaveMessage({ text: initialMsg, type: 'success' });

            if (willPropagate) {
              propagate(selectedFace.id, parseFloat(threshold) || 0.35)
                .then(async (result) => {
                  const propagatedCount = result?.updated?.length || 0;
                  // Refresh only the active photo's faces — same reasoning
                  // as the create/edit Save path (refreshPhotos resets the
                  // filmstrip's lazy-load offset and re-fires scrollIntoView).
                  await loadFaces(selectedImage);
                  const updated = Math.min(labeledBefore + 1 + propagatedCount, totalFaces);
                  let msg = `Saved! ${updated}/${totalFaces} faces labeled`;
                  if (propagatedCount > 0) msg += ` (+${propagatedCount} propagated)`;
                  setSaveMessage({ text: msg, type: 'success' });
                  setTimeout(() => setSaveMessage(null), 4000);
                })
                .catch((err) => {
                  console.error('Propagation failed:', err);
                  setTimeout(() => setSaveMessage(null), 4000);
                });
            } else {
              setTimeout(() => setSaveMessage(null), 4000);
            }
          } catch (err) {
            console.error('Assign from suggestion failed', err);
            setSaveMessage({
              text: `Assign failed: ${err?.response?.data?.detail || err?.message || 'unknown error'}`,
              type: 'error',
            });
          } finally {
            setSavingPerson(false);
          }
        }}
        onSelectNeighbor={async (row) => {
          // Clicking a Similar Persons row directly assigns that person to
          // the active face. No form fill, no second-click Save.
          if (!selectedFace?.id || !row?.person_id) return;
          try {
            await assignPerson(selectedFace.id, row.person_id, row.distance ?? null);
            await loadFaces(selectedImage);
            setShowPersonModal(false);
            setModalPersonId(null);
            setSaveMessage({
              text: `Saved! Assigned to ${row.name}.`,
              type: 'success',
            });
            setTimeout(() => setSaveMessage(null), 4000);
          } catch (err) {
            console.error('Assign from similar person failed', err);
            setSaveMessage({
              text: `Assign failed: ${err?.response?.data?.detail || err?.message || 'unknown error'}`,
              type: 'error',
            });
          }
        }}
        onSave={async () => {
          // P5: search phase has no Save action — the button label is
          // "Done"; clicking it just closes. The actual save happens
          // when the user clicks a suggestion (onSelectSuggestion) or
          // confirms in create/edit (this handler).
          if (personModalPhase === 'search') {
            setShowPersonModal(false);
            setModalPersonId(null);
            return;
          }
          setSavingPerson(true);
          setSaveMessage(null);
          try {
            let personId = modalPersonId || (selectedFace ? selectedFace.person : null);
            // Build payload without team_id (handled separately via membership)
            const { team_id, ...personData } = personForm;
            const payload = {
              ...personData,
              jersey_number: personForm.jersey_number === "" ? null : Number(personForm.jersey_number),
            };
            // Round 2 Goal 3.4: a MatchHub-only pick carries the player's
            // external_id forward so the create flow links to MatchHub in one
            // shot (Person.external_id + CompanyPersonNaming.confirmed_name).
            if (!personId && matchHubPrefill?.external_id) {
              payload.external_id = String(matchHubPrefill.external_id);
            }
            // display_name was a stored column on Person; dropped in
            // migration 0055. The server emits it as a computed
            // @property from prefix/first/last/suffix, so building it
            // client-side is now dead weight (and would just shadow
            // the property when sent in the request).
            if (personId) {
              await updatePerson(personId, payload);
            } else {
              const person = await createPerson(payload);
              personId = person.id;
              // After create, persist the MatchHub link explicitly so the
              // company override (CompanyPersonNaming.confirmed_name) lands.
              if (matchHubPrefill?.external_id && matchHubPrefill?.confirmed_name) {
                try {
                  const { linkPersonToMatchHub } = await import('../api');
                  await linkPersonToMatchHub(
                    personId,
                    matchHubPrefill.external_id,
                    matchHubPrefill.confirmed_name,
                  );
                } catch (linkErr) {
                  console.warn('Person→MatchHub link failed:', linkErr);
                }
              }
            }
            // Handle team membership
            if (team_id && personId) {
              // For existing persons, we may need to remove old membership first
              // For simplicity, just add the new one - API handles duplicates
              const jerseyNum = personForm.jersey_number === "" ? null : Number(personForm.jersey_number);
              await addTeamMember(team_id, personId, jerseyNum);
            }
            // P5 national-team toggle: link second membership when set.
            // P4 caps at 1 CLUB + 1 NATIONAL — collision surfaces as a
            // non-fatal warning so the Person + first membership stay.
            if (selectedNationalTeam?.id && personId) {
              try {
                await addTeamMember(selectedNationalTeam.id, personId, null);
              } catch (natErr) {
                console.warn('National team link failed:', natErr);
                setSaveMessage({
                  text: `Saved person, but national team link failed: ${natErr?.response?.data?.detail || natErr?.message || 'unknown error'}`,
                  type: 'error',
                });
              }
            }
            const totalFaces = faces.filter(f => !f.ignored).length;
            const labeledBefore = faces.filter(f => !f.ignored && f.person).length;
            if (personId) {
              if (selectedFace) {
                await assignPerson(selectedFace.id, personId, null);
              } else if (selectedImage) {
                await addPersonToPhoto(selectedImage, personId, pendingPersonPositionRef.current);
              }
              pendingPersonPositionRef.current = null;
              // Refresh in background - don't block modal close
              loadFaces(selectedImage).catch(e => console.error('Face refresh failed', e));
              refreshPhotos();
            }
            setShowPersonModal(false);
            setModalPersonId(null);
            const labeledAfter = Math.min(labeledBefore + 1, totalFaces);
            // Only propagate on first-time tagging — re-assigning an already
            // tagged face shouldn't re-fire propagation.
            const willPropagate = !!selectedFace && !!personId && !selectedFace.person;
            const saveMsg = willPropagate
              ? `Saved! ${labeledAfter}/${totalFaces} faces labeled. Propagating...`
              : `Saved! Person added.`;
            setSaveMessage({ text: saveMsg, type: 'success' });
            if (willPropagate) {
              propagate(selectedFace.id, parseFloat(threshold) || 0.35)
                .then(async (result) => {
                  const propagatedCount = result?.updated?.length || 0;
                  // Only refresh the active photo's faces. Refreshing the
                  // whole photo list (refreshPhotos) was resetting the
                  // filmstrip lazy-load offset to 0 + retriggering the
                  // active-tile scrollIntoView, which produced the visible
                  // "photo shifts several times" + the lost scroll position.
                  // Stale face counts on other photos' filmstrip thumbs are
                  // acceptable until the next list fetch.
                  await loadFaces(selectedImage);
                  const updated = Math.min(labeledBefore + 1 + propagatedCount, totalFaces);
                  let msg = `Saved! ${updated}/${totalFaces} faces labeled`;
                  if (propagatedCount > 0) msg += ` (+${propagatedCount} propagated)`;
                  setSaveMessage({ text: msg, type: 'success' });
                  setTimeout(() => setSaveMessage(null), 4000);
                })
                .catch((err) => {
                  console.error('Propagation failed:', err);
                  setTimeout(() => setSaveMessage(null), 4000);
                });
            } else {
              setTimeout(() => setSaveMessage(null), 4000);
            }
          } catch (e) {
            console.error(e);
            setSaveMessage({ text: "Save failed", type: "error" });
            setTimeout(() => setSaveMessage(null), 4000);
          } finally {
            setSavingPerson(false);
          }
        }}
        onClose={() => { setShowPersonModal(false); setModalPersonId(null); }}
        onDragStart={handleModalDragStart}
      />
      {/* Full resolution image viewer - uses extracted FullResViewer component */}
      {showFullRes && (
        <FullResViewer photo={selectedPhoto} onClose={() => setShowFullRes(false)} />
      )}

      {/* One-time notice about preview vs export differences */}
      <PreviewExportNotice hasAdjustments={!isDefaultAdjustments(adjustments)} />
    </div>
  );
}
